'use client'

/**
 * /wertung?preview=120 → Design-Vorschau mit 120 deterministischen Fake-Piloten
 * (kein DB-Anschluss, kein API-Call) — Stephan-Wunsch R100.47.
 */

/**
 * /wertung — FGL Saison-Wertung (R100.42, 2026-05-28)
 *
 * Sicht-Typen:
 *   1. OVERALL     — Bester Pilot Overall (3-Spalten: Club | Standard | Open)
 *   2. KLASSEN     — Club / Standard / Open (Min-5-Flüge-Gate)
 *   3. ZEIT        — 30 Min / 1h / 2h / 3h / +3h
 *   4. EINZELFLUG  — Klassen-übergreifend, sortiert nach höchster Punktzahl
 *
 * Rank-Hierarchie:
 *   Platz 1   → "1" + Pokal + Gold-Glow + Pulse
 *   Platz 2   → "Verfolger 1" + Silber-Glow
 *   Platz 3   → "Verfolger 2" + Bronze-Glow
 *   Platz 4-10→ "Verfolger 3…9" + abnehmender Glow
 *   Platz 11+ → "11", "12", … + kein Glow
 *
 * Strenge Terrain-Trennung FLATLAND vs SLOPE.
 * Flug-Cards: km + Speed + Speed-Batches + → Link, ohne Datum/Dauer/Klassen-Label.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { motion, AnimatePresence } from 'framer-motion'
import Link from 'next/link'
import { FglRaceTrack } from '@/components/wertung/fgl-race-track'
import { useTranslation } from '@/i18n/client'
import { useScrollShadow } from '@/hooks/use-scroll-shadow'
import { PrivacyGate } from '@/components/auth/privacy-gate'

// Login-State global im Wertung-Tree zugänglich — entscheidet ob Pilot-Names
// und Flug-Cards Links auf Detail-Seiten werden oder nur Text bleiben.
const LoginContext = createContext<boolean>(false)
const useLoggedIn = () => useContext(LoginContext)

// Pilot-Modal-Opener (für TOP 11-50 + 51+) — Klick auf Score öffnet Modal
const PilotModalContext = createContext<(row: PilotRow) => void>(() => {})
const usePilotModal = () => useContext(PilotModalContext)

// R103.38: Subtype-Registry-Context — Flug-Mini-Cards lookup'n Klartext + Farbe
const SubtypesRegistryContext = createContext<Record<string, SubtypeMeta>>({})
const useSubtypesRegistry = () => useContext(SubtypesRegistryContext)
import { FglHeader } from '@/components/layout/fgl-header'
import { FglFooter } from '@/components/layout/fgl-footer'
import { DonorAvatar } from '@/components/ui/donor-avatar'
import { Reveal, CountUp, GliderBackdrop, LenisProvider } from '@/components/award'
import { RecordBar } from '@/components/wertung/record-bar'
import { RecordChaseCard } from '@/components/wertung/record-chase-card'
import { TodaySubmittedColumn } from '@/components/wertung/today-submitted-column'
import {
  getWertungsRegelText,
  type WertungsBucket,
  type WertungsKlasse,
  type WertungsView,
} from '@/lib/wertungs-regeln'
// R103.38: Mini-SVG-Icon + Klartext-Fallback fuer Terrain-Subtype
import { TerrainSubtypeIcon, defaultDisplayNameFor } from '@/components/terrain/terrain-subtype-icon'
import {
  ChevronDown, ChevronRight, Mountain, Trees, Trophy, Medal, Award,
  Clock, Layers, Maximize2, X,
} from 'lucide-react'
import { getBadgeIcon, getBadgeTierStyle } from '@/lib/badge-styles'

type Terrain = 'FLATLAND' | 'SLOPE'
// R103.29 (T-Day, Stephan-Spec): UI gruppiert 'overall' und 'zeit' unter
// einem TopTab "Zeitwertung". Intern bleibt der View-Type aber 'overall'
// (= Zeit-Sub "Overall") fuer Vertraeglichkeit mit Sub-Komponenten +
// API-Endpunkt. Nur drei UI-Top-Tabs: Bester Einzelflug | Klassen | Zeitwertung.
type View = 'overall' | 'klasse' | 'zeit' | 'einzelflug'
type Klasse = 'CLUBCLASS' | 'STANDARD' | 'OPEN'
type Bucket = '30min' | '1h' | '2h' | '3h' | '3plus'

interface FlightCard {
  flightId: string
  date: string
  distanceKm: number
  avgKmh: number | null
  durationS: number | null
  classCode: Klasse | null
  reachedStages: Bucket[]
  speedBatches: string[]
  /** R103.38: technischer Subtype-String, UI macht Lookup gegen subtypesRegistry */
  terrainSubtype?: string | null
  /** r19.55.104 (Stephan-Spec 2026-08-06): Flug-Modell inkl. Ausfuehrung.
   *  aircraftLabel = "Hersteller Modell · Ausfuehrung". Optional weil
   *  Alt-Flug-Cards ohne aircraft. */
  aircraftLabel?: string | null
  aircraftCustomName?: string | null
  aircraftDriveType?: string | null
  aircraftHardness?: string | null
}

// R103.38: Subtype-Registry-Eintrag aus /api/public/wertung
interface SubtypeMeta {
  displayName: string | null
  colorHex: string
}

interface PilotMeta {
  userId: string
  name: string
  slug: string | null
  /** R102.6 (Stephan-Wunsch 2026-06-01): Echtname verlinkt aufs Profil NUR
   *  wenn der Pilot sein Profil öffentlich freigeschaltet hat. Sonst plain. */
  isPublic?: boolean
  avatarUrl: string | null
  donorTier: string | null
  countryCode?: string | null
  clubName?: string | null
}

interface PilotRow {
  rank: number
  pilot: PilotMeta
  scoreKm: number
  flights: FlightCard[]
  flightCount: number
  /** ISO-Datum des juengsten beitragenden Fluges = "haelt Position seit" */
  positionHeldSince: string | null
  /** ISO-2 Country-Code (für Stats-Header + Fahnen) */
  countryCode?: string | null
  /** r19.55.90 (Stephan-Spec 2026-08-05): Anwaerter-Modus in Klassenwertung.
   *  false = Pilot hat < 5 Fluege in dieser Klasse, wird grau + Tooltip
   *  gerendert. In Overall/Zeit immer true. */
  meetsClassMin?: boolean
  /** Wieviele Fluege noch fehlen bis Klassen-Qualifikation. 0 = qualifiziert. */
  missingFlights?: number
}

interface BaseResult {
  terrain: Terrain
  season: number
  totalPilots: number
  totalFlightsInPool: number
  nations?: { iso: string; pilotCount: number }[]
  rows: PilotRow[]
}

interface EinzelflugResult {
  terrain: Terrain
  season: number
  classFilter: Klasse | null
  totalFlights: number
  flights: { rank: number; pilot: PilotMeta; flight: FlightCard }[]
}

const KLASSE_LABEL: Record<Klasse, string> = {
  CLUBCLASS: 'Club',
  STANDARD: 'Standard',
  OPEN: 'Open',
}

// r19.55.216: BUCKET_LABEL wurde durch useBucketLabel() ersetzt (i18n via
// wertung.bucketLong.*). Diese Konstante bleibt als Fallback fuer wenige
// non-React-Kontexte (Sortierhilfen etc.) — Anzeige geht ueber i18n.
const BUCKET_LABEL: Record<Bucket, string> = {
  '30min': '30 Min',
  '1h': '1 Stunde',
  '2h': '2 Stunden',
  '3h': '3 Stunden',
  '3plus': '+3 Stunden',
}

const BUCKET_COLOR: Record<Bucket, string> = {
  '30min': '#7dd3fc',
  '1h': '#a78bfa',
  '2h': '#fbbf24',
  '3h': '#fb923c',
  '3plus': '#f43f5e',
}

// ════════════════════════════════════════════════════════════════
//   Rank-Glow + Verfolger-Label
// ════════════════════════════════════════════════════════════════

const RANK_PALETTE: Record<number, { ring: string; glow: string; bg: string }> = {
  1:  { ring: 'rgba(242,201,78,.85)',  glow: 'rgba(242,201,78,.55)', bg: 'rgba(242,201,78,.10)' },
  2:  { ring: 'rgba(225,225,235,.75)', glow: 'rgba(225,225,235,.45)', bg: 'rgba(225,225,235,.07)' },
  3:  { ring: 'rgba(205,127,50,.75)',  glow: 'rgba(205,127,50,.45)', bg: 'rgba(205,127,50,.07)' },
}

function rankLabel(rank: number): string {
  if (rank === 1) return '1'
  if (rank <= 10) return `${rank - 1}. Verfolger`
  return String(rank)
}

/**
 * useRankLabel — i18n-aware Variante von rankLabel.
 * Liefert für Rang 2-10 das übersetzte "{n}. Verfolger / Chaser",
 * für Rang 1 die "1" und für >10 die nackte Zahl.
 */
function useRankLabel() {
  const { t } = useTranslation()
  return (rank: number): string => {
    if (rank === 1) return '1'
    if (rank <= 10) return t('wertung.chaser', { n: rank - 1 })
    return String(rank)
  }
}

/** Welche CSS-Klasse trägt die rotierende Halo-Aura? */
function rankSpinClass(rank: number): string {
  if (rank === 1) return 'fgl-rank-spin-gold'
  if (rank === 2) return 'fgl-rank-spin-silver'
  if (rank === 3) return 'fgl-rank-spin-bronze'
  if (rank <= 10) return `fgl-rank-glow-fade-${rank}` // 4..10
  return ''
}

function rankStyle(rank: number): React.CSSProperties {
  if (rank > 10) return {}
  const p = RANK_PALETTE[rank] || RANK_PALETTE[3]
  return {
    // backgroundImage (NICHT background:) damit das Tailwind bg-[var(--bg-card)]
    // als solide Grund-Farbe erhalten bleibt — Segler fliegen HINTER der Card,
    // nicht DURCH sie. Der Gold/Silber/Bronze-Tint ist nur ein Overlay.
    backgroundImage: `linear-gradient(135deg, ${p.bg}, transparent 70%)`,
    borderColor: rank <= 3 ? p.ring : 'var(--border)',
  }
}

function trophyForRank(rank: number) {
  if (rank === 1) return <Trophy className="w-5 h-5 text-amber-400 drop-shadow-[0_0_8px_rgba(242,201,78,.7)]" />
  if (rank === 2) return <Medal className="w-5 h-5 text-zinc-300 drop-shadow-[0_0_6px_rgba(225,225,235,.6)]" />
  if (rank === 3) return <Award className="w-5 h-5 text-orange-400 drop-shadow-[0_0_6px_rgba(205,127,50,.6)]" />
  return null
}

/**
 * Thermik-Bubbles für TOP 3 — aufsteigende, leicht versetzte Punkte
 * (wie LivingThermal in klein, auf die Card-Größe komprimiert).
 * Gold/Silber/Bronze. Liegt absolute im Background, blockt nichts.
 */
function TopRankBubbles({ tier }: { tier: 'gold' | 'silver' | 'bronze' }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl" aria-hidden>
      {Array.from({ length: 9 }).map((_, i) => (
        <span
          key={i}
          className={`fgl-bubble fgl-bubble-${tier} fgl-bubble-i${i}`}
        />
      ))}
    </div>
  )
}

function tierForRank(rank: number): 'gold' | 'silver' | 'bronze' | null {
  if (rank === 1) return 'gold'
  if (rank === 2) return 'silver'
  if (rank === 3) return 'bronze'
  return null
}

// ════════════════════════════════════════════════════════════════
//   Helpers
// ════════════════════════════════════════════════════════════════

function fmtKm(km: number): string {
  return km.toFixed(2).replace('.', ',')
}

function fmtDate(iso: string): string {
  const [y, mo, d] = iso.split('-')
  return `${d}.${mo}.${y.slice(2)}`
}

/**
 * "Hält Position seit X Tagen und Y Stunden" — vollständig ausgeschrieben.
 *
 * r19.55.216: durch useFmtPositionHeldFor() ersetzt (i18n). Diese Funktion
 * bleibt fuer Legacy-Kontexte ohne Hook (deutsche Fallback-Formulierung).
 */
function fmtPositionHeldFor(iso: string | null): string | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  const now = Date.now()
  const diffMs = Math.max(0, now - then)
  const diffH = Math.floor(diffMs / 3600_000)
  const days = Math.floor(diffH / 24)
  const hours = diffH % 24
  const dStr = days === 1 ? '1 Tag' : `${days} Tagen`
  const hStr = hours === 1 ? '1 Stunde' : `${hours} Stunden`
  if (days === 0) return hStr
  if (hours === 0) return dStr
  return `${dStr} und ${hStr}`
}

/**
 * i18n-Variante: Liefert lokalisiertes "X Tagen und Y Stunden".
 * r19.55.216.
 */
function useFmtPositionHeldFor() {
  const { t } = useTranslation()
  return (iso: string | null): string | null => {
    if (!iso) return null
    const then = new Date(iso).getTime()
    const now = Date.now()
    const diffMs = Math.max(0, now - then)
    const diffH = Math.floor(diffMs / 3600_000)
    const days = Math.floor(diffH / 24)
    const hours = diffH % 24
    const dStr = days > 0 ? (days === 1 ? t('wertung.positionDayOne') : t('wertung.positionDaysMany', { days })) : ''
    const hStr = hours > 0 ? (hours === 1 ? t('wertung.positionHourOne') : t('wertung.positionHoursMany', { hours })) : ''
    if (!dStr && !hStr) return t('wertung.positionHoursMany', { hours: 0 })
    if (!dStr) return hStr
    if (!hStr) return dStr
    return `${dStr}${t('wertung.positionSep')}${hStr}`
  }
}

/** "DE" → Flagge SVG-URL */
const FLAG_BASE = 'https://flagcdn.com/'
function flagSrc(iso: string): string {
  return `${FLAG_BASE}${iso.toLowerCase()}.svg`
}

function pilotHref(slug: string | null, userId: string): string {
  return slug ? `/pilot/${slug}` : `/pilot/${userId}`
}

// ════════════════════════════════════════════════════════════════
//   Pills
// ════════════════════════════════════════════════════════════════

function StagePill({ b }: { b: Bucket }) {
  const { t } = useTranslation()
  // r19.55.220: Klartext-Tooltip pro Bucket-Pille (30 Minuten / 1 Stunde etc.).
  const stageTip =
    b === '30min' ? t('wertung.stageTipMinutes', { minutes: 30 }) :
    b === '1h'    ? t('wertung.stageTipHours', { hours: 1 }) :
    b === '2h'    ? t('wertung.stageTipHours', { hours: 2 }) :
    b === '3h'    ? t('wertung.stageTipHours', { hours: 3 }) :
                    t('wertung.stageTipPlusHours', { hours: 3 })
  return (
    <span
      // r19.55.78 (Stephan-Spec 2026-08-05): whitespace-nowrap + shrink-0
      // damit die Pille im 3-Spalten-Layout nicht zwischen Zahl und Buchstabe
      // umbricht (bei "3 H" / "2 H" / "3+ H").
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide whitespace-nowrap shrink-0"
      style={{
        background: `${BUCKET_COLOR[b]}22`,
        color: BUCKET_COLOR[b],
        border: `1px solid ${BUCKET_COLOR[b]}66`,
      }}
      data-tip={stageTip}
    >
      {t(`wertung.bucket.${b}`)}
    </span>
  )
}

function StagePills({ stages }: { stages: Bucket[] }) {
  const { t } = useTranslation()
  if (stages.length === 0) return null
  const order: Bucket[] = ['30min', '1h', '2h', '3h', '3plus']
  const sorted = order.filter(b => stages.includes(b))
  if (sorted.length === 0) return null
  // r19.55.75 (Stephan-Spec 2026-08-05): Nur HOECHSTE erreichte Zeitwertung
  // als Pille zeigen (analog Speed-Batch). Alle darunter sind impliziert und
  // wuerden das Layout in schmalen Spalten zerschiessen. Tooltip listet alle
  // erreichten Stufen fuer volle Transparenz.
  const highest = sorted[sorted.length - 1]
  const highestLabel = t(`wertung.bucket.${highest}`)
  const tip = sorted.length === 1
    ? t('wertung.stageTooltipSingle', { highest: highestLabel })
    : t('wertung.stageTooltipMulti', {
        highest: highestLabel,
        others: sorted.slice(0, -1).map(b => t(`wertung.bucket.${b}`)).join(', '),
      })
  return (
    <span data-tip={tip} className="inline-flex">
      <StagePill b={highest} />
    </span>
  )
}

/**
 * r19.55.49 (Stephan-Spec 2026-07-14): Terrain-Chip aufraeumen — nur EIN
 * Icon fuer Basis-Klasse (Flatland ✈ / Slope ⛰), ohne Subtype-Text.
 * Voller Subtype-Name (FLATLAND_HILLY, SLOPE_ALPINE_RIDGE, …) bleibt im
 * Tooltip. Reduziert visuelle Reizueberflutung in der Wertungs-Tabelle.
 */
function terrainBase(subtype: string | null | undefined): 'FLATLAND' | 'SLOPE' | null {
  if (!subtype) return null
  if (subtype.startsWith('FLATLAND')) return 'FLATLAND'
  if (subtype.startsWith('SLOPE')) return 'SLOPE'
  return null
}
/**
 * r19.55.219: liefert den lokalisierten Subtype-Anzeigenamen.
 * Aufloesungsreihenfolge:
 *   1) i18n-Key terrain.subtype.<CODE>  (bevorzugt, uebersetzt)
 *   2) DB-registry.display_name (nur wenn nicht identisch zum CODE)
 *   3) defaultDisplayNameFor(CODE) — deutscher Hardcoded-Fallback
 *   4) CODE selbst als letzter Notnagel
 */
function resolveSubtypeDisplay(
  t: (k: string, p?: Record<string, string | number>) => string,
  subtype: string | null,
  dbDisplay: string | null | undefined,
): string | null {
  if (!subtype) return null
  const key = `terrain.subtype.${subtype}`
  const fromI18n = t(key)
  if (fromI18n && fromI18n !== key) return fromI18n
  if (dbDisplay && dbDisplay !== subtype) return dbDisplay
  return defaultDisplayNameFor(subtype) || subtype
}

function TerrainBaseChip({ subtype, fullLabel }: { subtype: string | null; fullLabel: string | null }) {
  const { t } = useTranslation()
  const base = terrainBase(subtype)
  if (!base) return null
  // r19.55.59 (Stephan-Spec 2026-07-14): lucide-Icons Trees/Mountain
  // statt Emoji fuer konsistente Fernwirkung auf allen Plattformen.
  const Icon = base === 'FLATLAND' ? Trees : Mountain
  const baseLabel = t(`terrain.baseLabel.${base}`)
  const tip = fullLabel ? `${baseLabel} · ${fullLabel}` : baseLabel
  const color = base === 'FLATLAND' ? '#eab308' : '#a78bfa'
  return (
    <span
      className="wertung-flight-pill inline-flex items-center justify-center rounded-full"
      style={{
        ['--pill-color' as any]: color,
        width: 22,
        height: 22,
        color,
      }}
      data-tip={tip}
      aria-label={tip}
    >
      <Icon size={13} strokeWidth={2.2} />
    </span>
  )
}

/**
 * r19.55.49: Distance-Meilenstein-Pille in der Wertungs-Tabelle — analog
 * SpeedBatchPills, aber fuer Wertungsstrecke. Zeigt die HOECHSTE erreichte
 * Stufe (50/100/200/300/400/500). Kompakt, damit sie zur SpeedBatch-Groesse
 * passt. Farbpalette sky→violett zur optischen Trennung von der
 * warmen Speed-Palette.
 */
const DISTANCE_THRESHOLDS = [50, 100, 200, 300, 400, 500] as const
function DistanceMilestonePill({ distanceKm }: { distanceKm: number | null | undefined }) {
  const { t } = useTranslation()
  if (distanceKm == null || distanceKm <= 0) return null
  const earned = DISTANCE_THRESHOLDS.filter(t => distanceKm >= t)
  if (earned.length === 0) return null
  const top = Math.max(...earned)
  // Kuehle Farbtoene analog DistanceAchievementBadge
  const color =
    top >= 500 ? '#8b5cf6' :
    top >= 400 ? '#10b981' :
    top >= 300 ? '#14b8a6' :
    top >= 200 ? '#06b6d4' :
    top >= 100 ? '#3b82f6' :
                 '#0ea5e9'
  const tierKey =
    top >= 500 ? 'wertung.distanceMilestoneLegend' :
    top >= 400 ? 'wertung.distanceMilestoneQuadro' :
    top >= 300 ? 'wertung.distanceMilestoneTriple' :
    top >= 200 ? 'wertung.distanceMilestoneDouble' :
    top >= 100 ? 'wertung.distanceMilestoneCentury' :
                 'wertung.distanceMilestoneStarter'
  return (
    <span
      className="wertung-flight-pill px-2 py-0.5 rounded-full text-[10px] font-black tracking-wide"
      style={{ ['--pill-color' as any]: color }}
      data-tip={t('wertung.distanceMilestoneTip', {
        tier: t(tierKey),
        threshold: top,
        km: distanceKm.toFixed(2),
      })}
    >
      +{top}
    </span>
  )
}

/** Nur die HÖCHSTE Speed-Batch-Pille zeigen (Stephan-Spec R100.48). */
function SpeedBatchPills({ batches }: { batches: string[] }) {
  const { t } = useTranslation()
  if (!batches || batches.length === 0) return null
  const highest = batches[batches.length - 1] // batches sind aufsteigend sortiert
  // 2026-06-16 (Stephan-Spec): Eskalierende Tier-Animation. Nur 70/80/90/100
  // werden animiert. Höhere Werte (+110/+120/+130) rolled-up auf tier 100.
  const tierMatch = /^\+(\d+)$/.exec(highest)
  const num = tierMatch ? parseInt(tierMatch[1], 10) : 0
  const tier: 70 | 80 | 90 | 100 | null =
    num >= 100 ? 100 :
    num >= 90  ? 90  :
    num >= 80  ? 80  :
    num >= 70  ? 70  : null
  // r19.55.220: Tooltip pro Speed-Batch mit Tier-Erklaerung, i18n.
  const tierLabel =
    tier === 100 ? t('wertung.speedBatchChampion') :
    tier === 90  ? t('wertung.speedBatchGold') :
    tier === 80  ? t('wertung.speedBatchSilber') :
    tier === 70  ? t('wertung.speedBatchBronze') :
                   null
  const tip = tierLabel
    ? t('wertung.speedBatchTip', { tier: tierLabel, avgKmh: num })
    : t('wertung.speedBatchTipGeneric', { avgKmh: num })
  if (tier == null) {
    return (
      <span
        className="wertung-flight-pill px-2 py-0.5 rounded-full text-[10px] font-black tracking-wide"
        style={{ ['--pill-color' as any]: '#f2c94e' }}
        data-tip={tip}
      >
        {highest}
      </span>
    )
  }
  return (
    <span
      className={`wertung-flight-pill speed-batch-active speed-batch-tier-${tier} px-2 py-0.5 rounded-full text-[10px] font-black tracking-wide`}
      data-tip={tip}
    >
      {highest}
    </span>
  )
}

/** Kleine Flagge SVG aus flagcdn.com */
function FlagIcon({ iso, size = 18 }: { iso: string | null | undefined; size?: number }) {
  const c = (iso || '').toLowerCase().trim()
  if (!/^[a-z]{2}$/.test(c)) return null
  return (
    <img
      src={`${FLAG_BASE}${c}.svg`}
      alt={c.toUpperCase()}
      width={size}
      height={Math.round(size * 0.67)}
      className="inline-block rounded-[2px] object-cover shrink-0"
      loading="lazy"
      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
    />
  )
}

// ════════════════════════════════════════════════════════════════
//   Flug-Karte (kompakt)
// ════════════════════════════════════════════════════════════════

/**
 * ClassBadge — kleine Pille für die Materialklasse eines Fluges.
 * R100.52.1: Nur in Zeitwertung + Einzelflug sichtbar (dort klassen-übergreifend).
 * Farben passen zur Award-Palette: CLUBCLASS=gold-warm, STANDARD=blau, OPEN=violett.
 */
function ClassBadge({ classCode }: { classCode: Klasse }) {
  const { t } = useTranslation()
  const color: Record<Klasse, string> = {
    CLUBCLASS: '#f2c94e',
    STANDARD:  '#60a5fa',
    OPEN:      '#c084fc',
  }
  // r19.55.220: Klartext-Erklaerung der Materialklasse mit Spannweiten-Grenze.
  const tip =
    classCode === 'CLUBCLASS' ? t('wertung.classTipClub')
    : classCode === 'STANDARD' ? t('wertung.classTipStandard')
    : t('wertung.classTipOpen')
  return (
    <span
      data-tip={tip}
      className="wertung-flight-pill px-2 py-0.5 rounded-full text-[9px] font-black tracking-wider uppercase"
      style={{ ['--pill-color' as any]: color[classCode], letterSpacing: '.10em' }}
    >
      {KLASSE_LABEL[classCode]}
    </span>
  )
}

function FlightMiniCard({
  flight,
  showClass,
}: {
  flight: FlightCard
  /** Veraltete props bleiben kompatibel, werden ignoriert */
  contextBucket?: Bucket
  showStages?: boolean
  /** R100.52.1: Zeigt Klassen-Badge (Club/Standard/Open) — nur in Zeitwertung
      und Einzelflug aktiviert, weil dort klassen-übergreifend gewertet wird. */
  showClass?: boolean
}) {
  const { t } = useTranslation()
  // R103.38: Subtype-Klartext via Context-Registry (2-Stage-Fallback).
  // r19.55.219: 3-Stage-Fallback ueber i18n-Key + Registry + Hardcoded.
  const subtypesRegistry = useSubtypesRegistry()
  const subtype = flight.terrainSubtype || null
  const subtypeMeta = subtype ? subtypesRegistry[subtype] : undefined
  const subtypeLabel = resolveSubtypeDisplay(t, subtype, subtypeMeta?.displayName)
  // R102.7 (2026-06-01): Flüge wieder klickbar nach Stephan-Bug
  // („und die links zu den Flügen sind auch weg?"). Wertung ist auth-gated
  // (tier_gated/public). Link auf öffentliche Flug-Detail /flight/[id].
  return (
    <a
      href={`/flight/${flight.flightId}`}
      className="wertung-flight-card flex items-center gap-1.5 px-2 py-2 rounded-xl"
      data-tip={t('wertung.flightCardTooltip', {
        km: fmtKm(flight.distanceKm),
        avgKmh: flight.avgKmh != null ? ` · Ø ${Math.round(flight.avgKmh)} km/h` : '',
        date: flight.date ? ` · ${new Date(flight.date).toLocaleDateString('de-DE')}` : '',
        aircraft: flight.aircraftLabel ? ` · ${flight.aircraftLabel}` : '',
      })}
    >
      {/* r19.55.55 (Stephan-Spec 2026-07-14): Tooltips auf alle Werte
          (Distanz, Speed, Datum). r19.55.56: NUR data-tip, kein title -
          sonst doppelter Tooltip (nativer Browser + FGL-Sprechblase). */}
      {/* r19.55.79 (Stephan-Spec 2026-08-05): Fixed-width auf Distanz +
          Speed + Datum damit alle 3 Metriken bei allen Flügen an derselben
          X-Position stehen. Pillen sind dann alle bündig rechts (statt
          zickzack je nach km-Zahl-Breite). */}
      <div
        className="text-sm font-bold text-[var(--accent)] tabular-nums shrink-0 text-right"
        style={{ minWidth: 68 }}
        data-tip={t('wertung.scoringDistanceLabel', { km: fmtKm(flight.distanceKm) })}
      >
        {fmtKm(flight.distanceKm)} km
      </div>
      {flight.avgKmh != null && (
        <div
          className="text-[8px] text-[var(--muted)] tabular-nums shrink-0 text-right"
          style={{ minWidth: 34 }}
          data-tip={t('wertung.averageSpeedLabel', { avgKmh: Math.round(flight.avgKmh) })}
        >
          {Math.round(flight.avgKmh)} km/h
        </div>
      )}
      {flight.date && (
        <div
          className="text-[8px] text-[var(--muted)]/70 tabular-nums shrink-0 hidden sm:block text-right"
          style={{ minWidth: 28 }}
          data-tip={t('wertung.flightDateLabel', {
            date: new Date(flight.date).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' }),
          })}
        >
          {new Date(flight.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
        </div>
      )}
      {/* r19.55.110 (Stephan-Spec 2026-08-06): Modell-Zeile wieder raus —
          sprengte die FlightMiniCard-Breite. Modellname (inkl. Ausfuehrung)
          landet stattdessen im Card-Tooltip (data-tip auf <a>). */}
      <div className="flex-1" />
      {/* r19.55.49 (Stephan-Spec 2026-07-14): Terrain-Chip reduziert auf
          Basis-Icon (Flatland/Slope) — voller Subtype-Name ist im Tooltip.
          Plus neuer Distance-Meilenstein +50/+100/+200/... in gleicher
          Groesse wie SpeedBatchPills. */}
      <TerrainBaseChip subtype={subtype} fullLabel={subtypeLabel} />
      {showClass && flight.classCode && (
        <ClassBadge classCode={flight.classCode} />
      )}
      <DistanceMilestonePill distanceKm={flight.distanceKm} />
      <SpeedBatchPills batches={flight.speedBatches} />
      {/* r19.55.74 (Stephan-Spec 2026-07-31): Erreichte Zeitwertungs-Pillen.
          Zeigt aus welchen Zeitbuckets (30min/1h/2h/3h/+3h) der Flug in die
          Wertung eingeflossen ist - damit sichtbar wird dass die Klassen-
          Wertung sich unabhaengig der Zeitwertungen bewegt. */}
      {flight.reachedStages && flight.reachedStages.length > 0 && (
        <StagePills stages={flight.reachedStages} />
      )}
    </a>
  )
}

// ════════════════════════════════════════════════════════════════
//   PilotExtras — Modelle + Badges aus /api/public/pilots/[slug]
// ════════════════════════════════════════════════════════════════

interface PilotExtrasData {
  aircraft: Array<{
    manufacturer: string | null
    model_name: string | null
    span_mm: number | null
    auto_class_code: string | null
  }>
  badges: Array<{
    code?: string
    name?: string
    description?: string | null
    icon?: string | null
    tier?: string | null
    earnedAt?: string | null
  }>
}

const pilotExtrasCache = new Map<string, PilotExtrasData | 'loading' | 'error'>()

function usePilotExtras(slug: string | null): PilotExtrasData | null {
  const [, force] = useState(0)
  const cached = slug ? pilotExtrasCache.get(slug) : null
  useEffect(() => {
    if (!slug) return
    const cur = pilotExtrasCache.get(slug)
    if (cur) return
    pilotExtrasCache.set(slug, 'loading')
    fetch(`/api/public/pilots/${encodeURIComponent(slug)}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(json => {
        const data: PilotExtrasData = {
          aircraft: json?.data?.aircraft ?? [],
          badges: Array.isArray(json?.data?.badges) ? json.data.badges : [],
        }
        pilotExtrasCache.set(slug, data)
        force(x => x + 1)
      })
      .catch(() => {
        pilotExtrasCache.set(slug, 'error')
        force(x => x + 1)
      })
  }, [slug])
  if (!slug) return null
  if (!cached || cached === 'loading' || cached === 'error') return null
  return cached
}

function PilotExtras({ pilot }: { pilot: PilotMeta }) {
  const { t } = useTranslation()
  const data = usePilotExtras(pilot.slug)
  if (!data) return null
  const hasAircraft = data.aircraft.length > 0
  const hasBadges = data.badges.length > 0
  if (!hasAircraft && !hasBadges) return null

  return (
    <div className="mt-4 pt-3 border-t border-[var(--border)] space-y-3">
      {hasAircraft && (
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--muted)] mb-2">
            {t(
              data.aircraft.length === 1 ? 'wertung.hangarHeading' : 'wertung.hangarHeadingPlural',
              { n: data.aircraft.length },
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.aircraft.slice(0, 12).map((a, i) => {
              const label = [a.manufacturer, a.model_name].filter(Boolean).join(' ') || 'Modell'
              const span = a.span_mm ? `${(a.span_mm / 1000).toFixed(2)} m` : null
              const tipParts = [
                label,
                span ? `Spannweite ${span}` : null,
                a.auto_class_code ? `Klasse ${a.auto_class_code}` : null,
              ].filter(Boolean)
              const tipText = tipParts.join(' — ')
              return (
                <span
                  key={i}
                  data-tip={tipText}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--border)] bg-[var(--bg-soft)]/40 text-[11px] text-[var(--text)]"
                >
                  <span className="font-bold">{label}</span>
                  {span && <span className="text-[10px] text-[var(--muted)]">· {span}</span>}
                  {a.auto_class_code && (
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--accent)]">
                      · {a.auto_class_code}
                    </span>
                  )}
                </span>
              )
            })}
          </div>
        </div>
      )}
      {hasBadges && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--muted)]">
              {t('wertung.badgesHeading', { n: data.badges.length })}
            </div>
            <Link
              href="/app/badges"
              className="text-[10px] uppercase tracking-widest font-bold text-[var(--accent)] hover:underline"
            >
              {t('wertung.badgesAllLink')} →
            </Link>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.badges.slice(0, 16).map((b, i) => {
              const label = b.name || 'Badge'
              const IconComponent = getBadgeIcon(b.icon)
              const tierStyle = getBadgeTierStyle(b.tier)
              const tooltipText = b.description ? `${label} — ${b.description}` : label
              return (
                <span
                  key={b.code || i}
                  data-tip={tooltipText}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] transition-all"
                  style={{
                    background: tierStyle.rawBg,
                    borderColor: tierStyle.rawBorder,
                    color: 'var(--text)',
                  }}
                >
                  <IconComponent className="w-3.5 h-3.5 shrink-0" />
                  <span className="font-bold" style={{ color: tierStyle.rawColor }}>{label}</span>
                </span>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
//   Pilot-Row
// ════════════════════════════════════════════════════════════════

function PilotInfoModal({
  row, onClose, view,
}: {
  row: PilotRow | null
  /** R100.52.1: View-Kontext damit Klassen-Badge nur bei Zeitwertung gerendert wird */
  view?: View
  onClose: () => void
}) {
  const { t } = useTranslation()
  const fmtPos = useFmtPositionHeldFor()
  const loggedIn = useLoggedIn()
  useEffect(() => {
    if (!row) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [row, onClose])
  if (!row) return null

  const tier = tierForRank(row.rank)

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[fgl-modal-fade_.18s_ease-out]"
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className={`wertung-modal-glass relative w-full max-w-2xl rounded-3xl border bg-[var(--bg-card)] overflow-hidden ${tier ? `fgl-rank-spin-${tier}` : ''}`}
        style={tier ? rankStyle(row.rank) : { borderColor: 'var(--border)' }}
      >
        {tier && <TopRankBubbles tier={tier} />}

        <button
          onClick={onClose}
          aria-label="Schließen"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-[var(--bg-card)] border border-[var(--border)] flex items-center justify-center hover:border-[var(--accent)] hover:bg-[var(--accent)]/10 transition-all"
        >
          <X className="w-5 h-5 text-[var(--muted)]" />
        </button>

        <div className="p-6 sm:p-8 relative">
          {/* Header — Avatar + Name + Score. R106.3 (Stephan-Spec):
              Klick auf Header oeffnet die Pilotenseite /pilot/[slug]. */}
          {row.pilot.slug ? (
            <Link
              href={`/pilot/${row.pilot.slug}`}
              className="flex items-center gap-3 sm:gap-4 mb-6 transition-opacity hover:opacity-90 group cursor-pointer"
              title="Zur Pilotenseite"
            >
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-2xl sm:text-3xl font-black text-[var(--muted)] tabular-nums">
                  {row.rank}
                </span>
                {trophyForRank(row.rank)}
              </div>
              <DonorAvatar
                src={row.pilot.avatarUrl}
                name={row.pilot.name}
                donorTier={row.pilot.donorTier}
                size={56}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 font-black text-base sm:text-xl text-[var(--text)] group-hover:text-[var(--accent)] transition-colors">
                  <span className="truncate">{row.pilot.name}</span>
                  <FlagIcon iso={row.pilot.countryCode} size={18} />
                  <span className="text-[var(--muted)] group-hover:text-[var(--accent)] opacity-50 ml-1">→</span>
                </div>
                <div className="text-[10px] text-[var(--muted)] mt-1">
                  {row.flights.length < row.flightCount ? (
                    <>{t('wertung.bestFromValid', { used: row.flights.length, total: row.flightCount })}</>
                  ) : (
                    <>{row.flightCount} {t('wertung.validFlights')}</>
                  )}
                  {row.positionHeldSince && fmtPos(row.positionHeldSince) && (
                    <> · {t('wertung.positionHeldSince', { rank: row.rank, when: fmtPos(row.positionHeldSince) ?? '' })}</>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-2xl sm:text-3xl font-black tabular-nums leading-none" style={{ color: 'var(--hero-value-color, var(--accent))' }}>
                  {fmtKm(row.scoreKm)}
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold mt-1" style={{ color: 'var(--hero-label-color, var(--text-muted))' }}>
                  {t('wertung.points')}
                </div>
              </div>
            </Link>
          ) : (
            <div className="flex items-center gap-3 sm:gap-4 mb-6">
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-2xl sm:text-3xl font-black text-[var(--muted)] tabular-nums">
                  {row.rank}
                </span>
                {trophyForRank(row.rank)}
              </div>
              <DonorAvatar
                src={row.pilot.avatarUrl}
                name={row.pilot.name}
                donorTier={row.pilot.donorTier}
                size={56}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 font-black text-base sm:text-xl text-[var(--text)]">
                  <span className="truncate">{row.pilot.name}</span>
                  <FlagIcon iso={row.pilot.countryCode} size={18} />
                </div>
                <div className="text-[10px] text-[var(--muted)] mt-1">
                  {row.flights.length < row.flightCount ? (
                    <>{t('wertung.bestFromValid', { used: row.flights.length, total: row.flightCount })}</>
                  ) : (
                    <>{row.flightCount} {t('wertung.validFlights')}</>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-2xl sm:text-3xl font-black tabular-nums leading-none" style={{ color: 'var(--hero-value-color, var(--accent))' }}>
                  {fmtKm(row.scoreKm)}
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold mt-1" style={{ color: 'var(--hero-label-color, var(--text-muted))' }}>
                  {t('wertung.points')}
                </div>
              </div>
            </div>
          )}

          {/* r19.44 (Stephan-Spec 2026-06-25): Beitragende Flüge MIT Klick zu
              /flight/[id] AUCH für ausgeloggte sichtbar. Die Public-Flight-
              Detail-Seite ist von Haus aus public. Login-CTA bleibt als kleiner
              Hinweis unten für Profil/Stats-Mehrwert. */}
          {row.flights.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--muted)] mb-2">
                {t('wertung.bestFlightsHeading', { n: row.flights.length })}
              </div>
              <div className="space-y-1.5">
                {row.flights.map(f => (
                  <FlightMiniCard key={f.flightId} flight={f} showClass={view === 'zeit'} />
                ))}
              </div>
            </>
          )}
          {!loggedIn && (
            <div className="mt-4 text-center py-3 px-4 rounded-2xl bg-[var(--accent)]/5 border border-[var(--accent)]/20">
              <p className="text-[11px] text-[var(--muted)] mb-2">
                {t('wertung.loginCtaModal')}
              </p>
              <div className="flex gap-2 justify-center">
                <Link
                  href="/login"
                  className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest bg-[var(--accent)] text-[var(--award-ink)]"
                >
                  {t('wertung.loginButton')}
                </Link>
                <Link
                  href="/register"
                  className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border border-[var(--accent)]/40 text-[var(--accent)]"
                >
                  {t('wertung.registerButton')}
                </Link>
              </div>
            </div>
          )}

          {/* R106.3 (Stephan-Spec 2026-06-07): Hangar + Badges NICHT mehr im Modal —
              sind auf der Pilotenseite /pilot/[slug] sichtbar. Klick auf den
              Modal-Header oben springt dorthin. */}
        </div>
      </div>
    </div>
  )
}

function PilotRowCard({
  row, view, currentBucket, onOpenModal, isExpanded, onToggle,
}: {
  row: PilotRow
  view: View
  currentBucket?: Bucket
  onOpenModal?: (row: PilotRow) => void
  /** R100.52: gesteuerte Single-Open-Logik aus dem Parent (PilotList) */
  isExpanded?: boolean
  onToggle?: () => void
}) {
  const { t } = useTranslation()
  const rankLbl = useRankLabel()
  const fmtPos = useFmtPositionHeldFor()
  const loggedIn = useLoggedIn()
  // Fallback: wenn Parent kein gesteuertes Pair liefert, läuft die Card
  // mit lokalem State weiter (rückwärtskompatibel für CompactRest etc).
  const [localExpanded, setLocalExpanded] = useState(false)
  const expanded = isExpanded ?? localExpanded
  const toggle = onToggle ?? (() => setLocalExpanded(v => !v))
  const isOverall = view === 'overall'
  // void-suppress for unused param (kept for backwards-compat)
  void onOpenModal

  // r19.55.90 (Stephan-Spec 2026-08-05): Anwaerter-Modus. Piloten mit
  // < 5 Fluegen in dieser Klasse werden grau + reduziert + Tooltip
  // gerendert (statt gefiltert). Nur in Klassenwertung. Overall + Zeit +
  // Einzelflug haben meetsClassMin=true (keine Mindestanzahl).
  const isAnwaerter = row.meetsClassMin === false
  const anwaerterTip = isAnwaerter
    ? (row.missingFlights === 1 ? t('wertung.candidateTooltipOne', { name: row.pilot.name }) : t('wertung.candidateTooltipMany', { missing: row.missingFlights ?? 5, name: row.pilot.name }))
    : undefined

  // R100.81: Wrapper-Pattern — outer trägt den drop-shadow-Glow (folgt der
  // clip-path-Form), inner hat clip-path + background.
  // drop-shadow ist filter-basiert und respektiert clip-path, im Gegensatz
  // zu box-shadow (das wird vom clip-path abgeschnitten).
  // 2026-06-14 (Stephan-Spec "WOW Maximal aufpimpen"): Drop-Shadow-Stack
  // pro Rang massiv verstaerkt. Mehrere Shadow-Schichten (close + mid + far
  // halo) + warm-gold/silber/bronze Ambient. Layout (Parallelogramm, SailGP)
  // bleibt eingefroren — nur Tiefe + Glow gewachsen.
  const glowFilter = (() => {
    // r19.55.90: Anwaerter bekommen keinen Rang-Glow, nur grauer Schatten.
    if (isAnwaerter) {
      return 'drop-shadow(0 2px 4px rgba(0,0,0,0.20)) grayscale(0.55)'
    }
    if (row.rank === 1) {
      return [
        'drop-shadow(0 0 12px rgba(242,201,78,0.95))',
        'drop-shadow(0 0 32px rgba(242,201,78,0.55))',
        'drop-shadow(0 0 72px rgba(242,201,78,0.30))',
        'drop-shadow(0 10px 28px rgba(0,0,0,0.38))',
      ].join(' ')
    }
    if (row.rank === 2) {
      return [
        'drop-shadow(0 0 10px rgba(225,225,235,0.85))',
        'drop-shadow(0 0 26px rgba(225,225,235,0.45))',
        'drop-shadow(0 0 56px rgba(225,225,235,0.22))',
        'drop-shadow(0 8px 24px rgba(0,0,0,0.32))',
      ].join(' ')
    }
    if (row.rank === 3) {
      return [
        'drop-shadow(0 0 10px rgba(205,127,50,0.85))',
        'drop-shadow(0 0 26px rgba(205,127,50,0.45))',
        'drop-shadow(0 0 56px rgba(205,127,50,0.22))',
        'drop-shadow(0 8px 24px rgba(0,0,0,0.32))',
      ].join(' ')
    }
    if (row.rank <= 10) {
      // Linear abgestuft Rang 4..10 — Gold-Aura schwaecher aber sichtbar.
      const close = Math.max(0.20, 0.65 - (row.rank - 3) * 0.07)
      const mid = Math.max(0.10, 0.32 - (row.rank - 3) * 0.04)
      return [
        `drop-shadow(0 0 10px rgba(242,201,78,${close}))`,
        `drop-shadow(0 0 ${28 - (row.rank - 3) * 2}px rgba(242,201,78,${mid}))`,
        'drop-shadow(0 6px 18px rgba(0,0,0,0.22))',
      ].join(' ')
    }
    return 'drop-shadow(0 2px 4px rgba(0,0,0,0.18)) drop-shadow(0 8px 22px rgba(0,0,0,0.16))'
  })()
  return (
    <Reveal>
      <div
        className={`relative ${!isAnwaerter && row.rank <= 3 ? rankSpinClass(row.rank) : ''}`}
        style={{ filter: glowFilter, opacity: isAnwaerter ? 0.55 : 1 }}
        data-tip={anwaerterTip}
      >
      <div
        className="relative overflow-hidden border bg-[var(--bg-card)] transition-all"
        style={{
          ...rankStyle(row.rank),
          // R100.81 Parallelogramm + Pseudo-Rundung an den nicht-abgeschnittenen
          // Ecken (TOP-RIGHT + BOTTOM-LEFT) via Bezier-ähnlicher Polygon-Stützpunkte.
          clipPath: [
            'polygon(',
            '26px 0,',                       // TOP-LEFT-Schräge Anfang
            'calc(100% - 14px) 0,',          // TOP-RIGHT-Annäherung
            'calc(100% - 6px) 2px,',
            'calc(100% - 2px) 6px,',
            '100% 14px,',                    // TOP-RIGHT abgerundet (14px-Eck)
            '100% 100%,',
            'calc(100% - 26px) 100%,',       // BOTTOM-RIGHT-Schräge
            '14px 100%,',                    // BOTTOM-LEFT-Annäherung
            '6px calc(100% - 2px),',
            '2px calc(100% - 6px),',
            '0 calc(100% - 14px),',          // BOTTOM-LEFT abgerundet
            '0 0',
            ')',
          ].join(' '),
        }}
      >
        {/* Thermik-Bubbles für TOP 3 — Hintergrund-Animation */}
        {tierForRank(row.rank) && (
          <TopRankBubbles tier={tierForRank(row.rank)!} />
        )}
        {/* Header — Mobile-first Layout, Score auf eigener Spalte. */}
        <div
          className="flex items-center gap-2 sm:gap-3 pl-0 py-3 cursor-pointer hover:bg-white/[0.03] transition-colors"
          style={{ paddingRight: '32px' /* R100.79: damit Score nicht in die clip-path-Schräge unten rechts ragt */ }}
          onClick={toggle}
        >
          {/* SailGP-Style Rank-Strip links — Clash-Display-Brand-Font,
              ~80% der Pillen-Höhe groß (Stephan-Spec R100.80).
              r19.55.90: Anwaerter (< 5 Fluege in Klasse) bekommen dezenten
              grauen Streifen + "ANWÄRTER" Label statt Gold + Rang-Zahl. */}
          <div
            className="flex flex-col items-center justify-center self-stretch shrink-0 tabular-nums"
            style={{
              background: isAnwaerter
                ? 'linear-gradient(180deg, color-mix(in oklch, var(--muted) 20%, transparent), color-mix(in oklch, var(--muted) 8%, transparent))'
                : 'linear-gradient(180deg, color-mix(in oklch, var(--award-gold) 32%, transparent), color-mix(in oklch, var(--award-gold) 18%, transparent))',
              color: isAnwaerter ? 'var(--muted)' : 'var(--award-gold)',
              minWidth: row.rank < 10 ? '88px' : '112px',
              paddingLeft: '26px',
              paddingRight: '12px',
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              fontSize: isAnwaerter
                ? 'clamp(24px, 3.5vw, 32px)'
                : (row.rank < 10 ? 'clamp(44px, 7vw, 64px)' : 'clamp(36px, 5.5vw, 52px)'),
              lineHeight: 1,
              letterSpacing: '-0.02em',
              borderRight: isAnwaerter
                ? '1px solid color-mix(in oklch, var(--muted) 20%, transparent)'
                : '1px solid color-mix(in oklch, var(--award-gold) 25%, transparent)',
            }}
          >
            {row.rank}
            {isAnwaerter && (
              <span
                className="mt-0.5 uppercase tracking-widest"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 900,
                  fontSize: '7px',
                  letterSpacing: '0.14em',
                  opacity: 0.85,
                }}
              >
                {t('wertung.candidateLabel')}
              </span>
            )}
          </div>

          {/* Trophy für TOP-3, kompakt */}
          {row.rank <= 3 && (
            <div className="hidden sm:flex items-center shrink-0 ml-1">{trophyForRank(row.rank)}</div>
          )}

          {/* Avatar — kleiner auf Mobile */}
          <div className="shrink-0 sm:hidden">
            <DonorAvatar
              src={row.pilot.avatarUrl}
              name={row.pilot.name}
              donorTier={row.pilot.donorTier}
              size={36}
            />
          </div>
          <div className="hidden sm:block shrink-0">
            <DonorAvatar
              src={row.pilot.avatarUrl}
              name={row.pilot.name}
              donorTier={row.pilot.donorTier}
              size={52}
            />
          </div>

          {/* R100.80 (Stephan-Spec): NUR EINE Flagge rechts, volle Höhe der Pille,
              direkt vor der Punktzahl. Layout:
              [Rank] · [Trophy] · [Avatar] · [Name+Untertitel] · [FLAG-FULL-HEIGHT] · [Score] */}
          <div className="flex-1 min-w-0 flex items-stretch">
            {/* Name + Untertitel + Score-Block in einer Zeile */}
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <div className="flex items-stretch gap-2 sm:gap-3">
                {(() => {
                  // R100.80: Nachname FETT, Vorname normal (Stephan-Spec / SailGP-Look)
                  const parts = (row.pilot.name || '').trim().split(/\s+/).filter(Boolean)
                  const last = parts.length > 0 ? parts[parts.length - 1] : ''
                  const first = parts.length > 1 ? parts.slice(0, -1).join(' ') : ''
                  const nameStyle: React.CSSProperties = {
                    fontFamily: 'var(--font-display)',
                    fontSize: 'clamp(20px, 2.6vw, 28px)',
                    letterSpacing: '-0.015em',
                    lineHeight: 1.05,
                    textTransform: 'uppercase',
                  }
                  const nameInner = (
                    <span className="truncate">
                      {first && <span style={{ fontWeight: 400 }}>{first} </span>}
                      <span style={{ fontWeight: 800 }}>{last}</span>
                    </span>
                  )
                  // R102.7 (Stephan-Wunsch 2026-06-01 abends): Wertung wieder
                  // public einsehbar. Link auf Pilotenseite zeigt für ALLE
                  // Viewer (auch ausgeloggt) — vorausgesetzt das Profil ist
                  // öffentlich freigegeben (Privacy by Choice). Einzelflüge
                  // bleiben weiterhin eingeloggten Piloten vorbehalten.
                  const canLink = !!row.pilot.isPublic && !!row.pilot.slug
                  return canLink ? (
                    <Link
                      href={pilotHref(row.pilot.slug, row.pilot.userId)}
                      onClick={e => e.stopPropagation()}
                      className="flex items-center gap-1.5 flex-1 min-w-0 text-[var(--text)] hover:text-[var(--accent)] transition-colors"
                      style={nameStyle}
                    >
                      {nameInner}
                      {row.rank <= 3 && <span className="shrink-0 sm:hidden">{trophyForRank(row.rank)}</span>}
                    </Link>
                  ) : (
                    <div
                      className="flex items-center gap-1.5 flex-1 min-w-0 text-[var(--text)]"
                      style={nameStyle}
                    >
                      {nameInner}
                      {row.rank <= 3 && <span className="shrink-0 sm:hidden">{trophyForRank(row.rank)}</span>}
                    </div>
                  )
                })()}
                {/* Full-height Flagge rechts, direkt vor der Punktzahl */}
                {row.pilot.countryCode && /^[a-z]{2}$/i.test(row.pilot.countryCode) && (
                  <img
                    src={`${FLAG_BASE}${row.pilot.countryCode.toLowerCase()}.svg`}
                    alt={row.pilot.countryCode.toUpperCase()}
                    className="shrink-0 object-cover self-center"
                    style={{
                      height: '52px',
                      width: '78px',
                      borderRadius: 4,
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.18)',
                    }}
                    loading="lazy"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                  />
                )}
                <div className="text-right shrink-0 self-center">
                  <div className="text-base sm:text-2xl font-bold sm:font-black tabular-nums leading-none" style={{ color: 'var(--hero-value-color, var(--accent))' }}>
                    {fmtKm(row.scoreKm)}
                  </div>
                  <div className="text-[8px] sm:text-[10px] uppercase tracking-widest font-bold mt-0.5" style={{ color: 'var(--hero-label-color, var(--text-muted))' }}>
                    {t('wertung.points')}
                  </div>
                </div>
              </div>

            {/* Zweite Zeile: kompakter Untertitel mit Verfolger-Label */}
            <div className="text-[10px] sm:text-[11px] text-[var(--muted)] mt-0.5 leading-snug">
              {row.rank > 1 && row.rank <= 10 && (
                <span className="font-bold">{rankLbl(row.rank)}{' · '}</span>
              )}
              {row.flights.length < row.flightCount ? (
                <span>{t('wertung.bestFromValid', { used: row.flights.length, total: row.flightCount })}</span>
              ) : (
                <span>{row.flightCount} {t('wertung.validFlights')}</span>
              )}
              {row.positionHeldSince && fmtPos(row.positionHeldSince) && (
                <span className="text-[var(--accent)]/80">
                  {' · '}{t('wertung.positionHeldSince', { rank: row.rank, when: fmtPos(row.positionHeldSince) ?? '' })}
                </span>
              )}
            </div>
            </div>
          </div>

          <motion.span
            animate={{ rotate: expanded ? 180 : 0 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            className="inline-flex items-center justify-center shrink-0"
          >
            <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--muted)]" />
          </motion.span>
        </div>

        {/* Expanded Body — Flug-Details nur eingeloggt sichtbar.
            R100.52: elegantes Slide-Open/Close via framer-motion
            (gleicher Ease wie SmoothAccordion + Sidebar). */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                height: { duration: 0.36, ease: [0.32, 0.72, 0, 1] },
                opacity: { duration: 0.24, ease: 'easeOut' },
              }}
              style={{ overflow: 'hidden' }}
            >
              <div className="px-4 pb-4 pt-2 border-t border-[var(--border)]">
                {/* r19.49.32 (Stephan-Spec 2026-07-01): Flug-Liste + Verlinkung zu
                     /flight/[id] AUCH fuer Ausgeloggte sichtbar. Public-Flight-
                     Detail ist von Haus aus oeffentlich, deshalb kein Grund mehr
                     hier zu blocken. Login-CTA nur noch dezent unter der Liste
                     als Hinweis auf Pilot-Profil-Mehrwert. */}
                {row.flights.length > 0 && (
                  // r19.55.89 (Stephan-Spec 2026-08-05): Zeit-Wertungen jetzt
                  // AUCH 3-spaltig CLUB / STANDARD / OPEN (analog Overall).
                  // Nur Klasse-View (view === 'klasse') bleibt einspaltig, weil
                  // die Klasse dort bereits als Sub-Filter aktiv ist.
                  (isOverall || view === 'zeit') ? (
                    <OverallThreeColumns
                      flights={row.flights}
                      contextBucket={view === 'zeit' ? currentBucket : undefined}
                    />
                  ) : (
                    <div className="space-y-1.5">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--muted)] mb-2">
                        {t('wertung.contributingFlightsHeading', { n: row.flights.length })}
                      </div>
                      {/* In diesem else-Branch ist view garantiert 'klasse'
                          oder 'einzelflug' (Zeit + Overall wurden oben abge-
                          fangen). Daher keine Bucket-/Class-Pillen noetig. */}
                      {row.flights.map(f => (
                        <FlightMiniCard key={f.flightId} flight={f} />
                      ))}
                    </div>
                  )
                )}
                {!loggedIn && (
                  <div className="mt-3 text-center py-2 px-3 rounded-xl bg-[var(--accent)]/5 border border-[var(--accent)]/20">
                    <p className="text-[11px] text-[var(--muted)] mb-2">
                      {t('wertung.loginCtaCard')}
                    </p>
                    <div className="flex gap-2 justify-center">
                      <Link
                        href="/login"
                        onClick={e => e.stopPropagation()}
                        className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest bg-[var(--accent)] text-[var(--award-ink)]"
                      >
                        {t('wertung.loginButton')}
                      </Link>
                      <Link
                        href="/register"
                        onClick={e => e.stopPropagation()}
                        className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border border-[var(--accent)]/40 text-[var(--accent)]"
                      >
                        {t('wertung.registerButton')}
                      </Link>
                    </div>
                  </div>
                )}
                {/* R100.81: PilotExtras (Hangar + Badges) entfernt aus Top-10-Accordion
                     (Stephan-Spec). Badges + Hangar bleiben auf /pilot/[slug] sichtbar. */}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </div>
    </Reveal>
  )
}

// ════════════════════════════════════════════════════════════════
//   Overall: 3-Spalten-Layout (Club | Standard | Open)
// ════════════════════════════════════════════════════════════════

function OverallThreeColumns({
  flights,
  contextBucket,
}: {
  flights: FlightCard[]
  /** r19.55.89: für Zeit-View setzt der Parent den aktiven Bucket durch,
   *  damit die MiniCard-Pille „Höchste Zeitwertung" beim Zeit-Bucket bleibt. */
  contextBucket?: Bucket
}) {
  const { t } = useTranslation()
  const byClass: Record<Klasse, FlightCard[]> = { CLUBCLASS: [], STANDARD: [], OPEN: [] }
  for (const f of flights) {
    if (f.classCode && byClass[f.classCode]) {
      byClass[f.classCode].push(f)
    }
  }
  // Top 5 pro Klasse, sortiert nach distanceKm
  const orderKlassen: Klasse[] = ['CLUBCLASS', 'STANDARD', 'OPEN']
  for (const k of orderKlassen) {
    byClass[k] = byClass[k].sort((a, b) => b.distanceKm - a.distanceKm).slice(0, 5)
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {orderKlassen.map(k => (
        <div key={k} className="space-y-1.5">
          <div className="flex items-center justify-between px-2 mb-1">
            <span className="text-[10px] uppercase tracking-widest font-black text-[var(--accent)]">
              {k === 'CLUBCLASS' ? t('wertung.classClub') : k === 'STANDARD' ? t('wertung.classStandard') : t('wertung.classOpen')}
            </span>
            {/* r19.49.21 (Stephan-Spec 2026-06-30 spätabend): „Top N" dynamisch
                aus tatsächlicher Anzahl. Vorher hardcoded „Top 5" auch wenn
                Pilot nur 3-4 Flüge in der Klasse hatte. */}
            <span className="text-[9px] uppercase tracking-widest font-bold text-[var(--muted)]">
              {t('wertung.topNFlights', { count: byClass[k].length || 5 })}
            </span>
          </div>
          {byClass[k].length === 0 ? (
            <div className="px-3 py-3 text-center text-[11px] text-[var(--muted)]/60 italic border border-dashed border-[var(--border)] rounded-lg">
              {t('wertung.noFlightsInClass')}
            </div>
          ) : (
            byClass[k].map(f => (
              <FlightMiniCard
                key={f.flightId}
                flight={f}
                showStages={true}
                contextBucket={contextBucket}
              />
            ))
          )}
        </div>
      ))}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
//   Fake-Data-Generator für Design-Vorschau (?preview=N)
// ════════════════════════════════════════════════════════════════

const FAKE_FIRST_NAMES = [
  'Jonas', 'Ariel', 'Wolfgang', 'Daniel', 'Steffen', 'Wolfram', 'Michael',
  'Stephan', 'Andreas', 'Markus', 'Christian', 'Thomas', 'Peter', 'Klaus',
  'Heinz', 'Jürgen', 'Bernd', 'Wolfgang', 'Hans', 'Otto', 'Werner',
  'Sven', 'Lukas', 'Maximilian', 'Felix', 'Tobias', 'Philipp', 'Florian',
  'Sebastian', 'Alexander', 'Robert', 'Martin', 'David', 'Patrick', 'Nicolas',
  'Marvin', 'Niklas', 'Jan', 'Dirk', 'Frank', 'Ralf', 'Holger', 'Achim',
  'Mathias', 'Stefan', 'Carsten', 'Volker', 'Uwe', 'Rolf', 'Erik', 'Lars',
  'Jens', 'Dominik', 'Tim', 'Kai', 'Henrik', 'Joachim', 'Manfred', 'Reinhold',
  'Hubert', 'Eberhard', 'Friedrich', 'Albrecht', 'Lothar', 'Adrian',
  'Pierre', 'Henri', 'Marc', 'Olivier', 'Jean', 'Luca', 'Marco', 'Giovanni',
  'Diego', 'Pablo', 'Alex', 'Tom', 'Ben', 'Jack',
]
const FAKE_LAST_NAMES = [
  'Wölfel', 'Dittrich', 'Schlüter', 'Wigger', 'Gralke', 'Just', 'Neu',
  'Müller', 'Schmidt', 'Schneider', 'Fischer', 'Weber', 'Meyer', 'Wagner',
  'Becker', 'Schulz', 'Hoffmann', 'Schäfer', 'Bauer', 'Koch', 'Richter',
  'Klein', 'Wolf', 'Schröder', 'Neumann', 'Schwarz', 'Zimmermann', 'Braun',
  'Krüger', 'Hofmann', 'Hartmann', 'Lange', 'Schmitt', 'Werner', 'Schmitz',
  'Krause', 'Meier', 'Lehmann', 'Schmid', 'Schulze', 'Maier', 'Köhler',
  'Herrmann', 'König', 'Walter', 'Bauer', 'Eich', 'Berg', 'Stein', 'Vogel',
  'Frank', 'Dubois', 'Martin', 'Bernard', 'Rossi', 'Bianchi', 'García',
]
const FAKE_NATIONS = ['de','de','de','de','at','ch','fr','it','nl','pl','dk','se','no','fi','cz']
const FAKE_TIERS = ['NONE','NONE','NONE','NONE','NONE','SUPPORTER','UNTERSTUETZER','FOERDERER','SUPPORTER_MONTHLY','GOENNER_MONTHLY']

/** Deterministisches Pseudo-Random aus seed (für reproduzierbare Previews). */
function mulberry32(seed: number) {
  return () => {
    seed |= 0; seed = seed + 0x6D2B79F5 | 0
    let t = Math.imul(seed ^ seed >>> 15, 1 | seed)
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t
    return ((t ^ t >>> 14) >>> 0) / 4294967296
  }
}

function generateFakePilots(count: number): BaseResult {
  const rand = mulberry32(42 + count)

  // Score-Verteilung: Rang 1 ≈ 600 km, exponentiell abnehmend
  const rows: PilotRow[] = []
  for (let i = 0; i < count; i++) {
    const rank = i + 1
    const baseScore = 600 * Math.exp(-i / 18) + 5
    const scoreKm = Math.round((baseScore + rand() * 8 - 4) * 100) / 100
    const first = FAKE_FIRST_NAMES[Math.floor(rand() * FAKE_FIRST_NAMES.length)]
    const last  = FAKE_LAST_NAMES[Math.floor(rand() * FAKE_LAST_NAMES.length)]
    const name = `${first} ${last}`
    const slug = `${first.toLowerCase()}-${last.toLowerCase().normalize('NFD').replace(/[^a-z]/g, '')}-${i}`
    const tier = FAKE_TIERS[Math.floor(rand() * FAKE_TIERS.length)]
    const country = FAKE_NATIONS[Math.floor(rand() * FAKE_NATIONS.length)]

    // PositionHeldSince: zwischen 0h und 60 Tagen zurück
    const heldHours = Math.floor(rand() * 24 * 60)
    const heldDate = new Date(Date.now() - heldHours * 3600_000)

    // Fake Top-5 contributing flights
    const flightCount = Math.max(5, Math.round(20 - i / 8 + rand() * 6))
    const flights: FlightCard[] = []
    for (let j = 0; j < 5; j++) {
      const km = Math.round((scoreKm / 5) * (1.2 - j * 0.08) * 100) / 100
      const avgKmh = 45 + Math.round(rand() * 50)
      const klass: Klasse = (['CLUBCLASS','STANDARD','OPEN'] as Klasse[])[Math.floor(rand() * 3)]
      const fdate = new Date(Date.now() - (rand() * 90 + j * 5) * 86400_000)
      const reached: Bucket[] = []
      const dur = 1800 + rand() * 10000
      if (dur >= 1800) reached.push('30min')
      if (dur >= 3600) reached.push('1h')
      if (dur >= 7200) reached.push('2h')
      if (dur >= 10800) reached.push('3h')
      const batches: string[] = []
      if (avgKmh >= 70)  batches.push('+70')
      if (avgKmh >= 80)  batches.push('+80')
      if (avgKmh >= 90)  batches.push('+90')
      if (avgKmh >= 100) batches.push('+100')
      flights.push({
        flightId: `fake-${i}-${j}`,
        date: fdate.toISOString().slice(0, 10),
        distanceKm: km,
        avgKmh,
        durationS: Math.round(dur),
        classCode: klass,
        reachedStages: reached,
        speedBatches: batches,
      })
    }

    rows.push({
      rank,
      pilot: {
        userId: `fake-pilot-${i}`,
        name,
        slug,
        // R102.6 (2026-06-01): Demo-Daten erlauben Profil-Link
        isPublic: true,
        avatarUrl: null,
        donorTier: tier,
        countryCode: country,
        clubName: null,
      },
      scoreKm,
      flights,
      flightCount,
      positionHeldSince: heldDate.toISOString(),
      countryCode: country,
    })
  }

  // Nations aus Pilot-Liste extrahieren
  const byNation = new Map<string, number>()
  for (const r of rows) {
    const c = r.countryCode || 'de'
    byNation.set(c, (byNation.get(c) || 0) + 1)
  }
  const nations = Array.from(byNation.entries())
    .map(([iso, pilotCount]) => ({ iso, pilotCount }))
    .sort((a, b) => b.pilotCount - a.pilotCount)

  return {
    terrain: 'FLATLAND',
    season: new Date().getFullYear(),
    totalPilots: rows.length,
    totalFlightsInPool: rows.reduce((s, r) => s + r.flightCount, 0),
    nations,
    rows,
  }
}

function generateFakeEinzelflug(count: number): EinzelflugResult {
  const base = generateFakePilots(count)
  // Pro Pilot: bester Flug = sortiert nach distanceKm
  const flights = base.rows
    .map((row, i) => ({
      rank: i + 1,
      pilot: row.pilot,
      flight: row.flights.sort((a, b) => b.distanceKm - a.distanceKm)[0],
    }))
    .filter(x => x.flight)
    .sort((a, b) => b.flight.distanceKm - a.flight.distanceKm)
    .map((x, i) => ({ ...x, rank: i + 1 }))
  return {
    terrain: 'FLATLAND',
    season: new Date().getFullYear(),
    classFilter: null,
    totalFlights: flights.length,
    flights,
  }
}

// ════════════════════════════════════════════════════════════════
//   Hauptseite
// ════════════════════════════════════════════════════════════════

export default function WertungPageWrapper() {
  // R102.7 (Stephan-Wunsch 2026-06-01): Wertung wieder PUBLIC einsehbar —
  // ausgeloggte Besucher sehen Rangliste + Pilot-Namen + Link aufs Profil
  // (falls dort öffentlich freigegeben). Die EINZELFLÜGE im Akkordeon
  // bleiben weiterhin nur für eingeloggte Piloten sichtbar (siehe loggedIn-
  // Gate in PilotRowCard + PilotModal). „So kann jeder externe sehen wie
  // aktuell der Stand ist."
  //
  // r19.55.206 GEO: SportsEvent-JSON-LD wird HIER im Wrapper gerendert (nicht
  // tief in WertungPage), damit der SSR-Server das Script sicher im initialen
  // HTML ausliefert — auch bevor die WertungPage-Suspense-Boundary hydratiert.
  const season = new Date().getFullYear()
  const sportsEventJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: `FREE GLIDE LEAGUE — Saison ${season}`,
    alternateName: `FGL Wertung ${season}`,
    description: 'Internationale Online-Vergleichsserie für RC-Modellsegelflug. Piloten reichen IGC-Flüge ein, die automatisch nach Klasse (Club/Standard/Open), Terrain (Flatland/Slope) und Zeitwertung (30 min bis 3+ h) aggregiert werden.',
    sport: 'RC Model Glider Soaring',
    url: 'https://free-glide-league.com/wertung',
    startDate: `${season}-01-01`,
    endDate: `${season}-12-31`,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    location: { '@type': 'VirtualLocation', url: 'https://free-glide-league.com/wertung' },
    organizer: {
      '@type': 'SportsOrganization',
      name: 'FREE GLIDE LEAGUE',
      url: 'https://free-glide-league.com',
    },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      url: 'https://free-glide-league.com/register',
      description: 'Kostenlose Teilnahme — kein Abo, keine Karte. Anmeldung via IGC-Upload freigeschaltet.',
    },
  }
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(sportsEventJsonLd) }}
      />
      <WertungPage />
    </>
  )
}

function WertungPage() {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const isLoggedIn = !!session?.user
  const sp = useSearchParams()

  // R106.3 v2 (2026-06-07): Polling-basierter Scroll-Motion-Trail (Lenis-safe)
  const scrollShadow = useScrollShadow({ maxOffset: 32, maxOpacity: 0.85 })
  const [modalRow, setModalRow] = useState<PilotRow | null>(null)
  const previewCount = useMemo(() => {
    const raw = sp?.get('preview')
    if (!raw) return null
    const n = parseInt(raw, 10)
    return Number.isFinite(n) && n > 0 ? Math.min(n, 500) : null
  }, [sp])

  // R103.46 + R103.54 Bugfix: terrain aus URL-Param (?terrain=slope|flatland)
  // damit Klick aus dem Newsticker direkt die richtige Wertung zeigt.
  // Dep auf sp damit Client-Side-Navigation auf /wertung?terrain=... vom Ticker
  // funktioniert (kein Page-Remount → ohne sp-Dep wuerde useEffect nur einmal
  // beim initialen Mount laufen, spaetere URL-Wechsel wuerden ignoriert).
  const [terrain, setTerrain] = useState<Terrain>('FLATLAND')
  useEffect(() => {
    const t = sp?.get('terrain')
    if (t === 'slope') setTerrain('SLOPE')
    else if (t === 'flatland') setTerrain('FLATLAND')
  }, [sp])
  // R103.29 (T-Day, Stephan-Spec): Default-View bleibt 'overall' (= Zeitwertung →
  // Sub "Overall"). Reihenfolge der UI-TopTabs: Einzelflug | Klassen | Zeitwertung.
  // r19.49 (Stephan-Bug 2026-06-29): Deep-Link von der Frontpage-Race-Track muss
  // den View SCHON im useState-Initializer aus URL lesen — wenn man ihn erst in
  // einem useEffect setzen würde, liefe ein erster Fetch mit Default-view
  // ('overall') gegen den View-Change-Fetch und die nachträglich eintreffende
  // BaseResult-Response würde die EinzelflugResult überschreiben → leere Seite.
  const initialView: View = (() => {
    const v = sp?.get('view')
    if (v === 'overall' || v === 'einzelflug' || v === 'klasse' || v === 'zeit') return v as View
    return 'overall'
  })()
  const initialKlasse: Klasse = (() => {
    const c = sp?.get('class')
    if (c === 'CLUBCLASS' || c === 'STANDARD' || c === 'OPEN') return c as Klasse
    return 'CLUBCLASS'
  })()
  const [view, setView] = useState<View>(initialView)
  const [klasse, setKlasse] = useState<Klasse>(initialKlasse)
  // Falls sich die URL-Params bei einer Client-Side-Navigation ändern (z.B.
  // Avatar-Klick auf einer anderen /wertung-Seite öffnet via Link), View+Klasse
  // entsprechend nachziehen.
  useEffect(() => {
    const v = sp?.get('view')
    if (v === 'overall' || v === 'einzelflug' || v === 'klasse' || v === 'zeit') {
      setView(v as View)
    }
    const c = sp?.get('class')
    if (c === 'CLUBCLASS' || c === 'STANDARD' || c === 'OPEN') {
      setKlasse(c as Klasse)
    }
  }, [sp])
  const [bucket, setBucket] = useState<Bucket>('30min')
  const [season] = useState<number>(new Date().getFullYear())

  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<BaseResult | EinzelflugResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  // R103.38: Subtype-Registry kommt mit jeder Wertungs-Response — Lookup-Map fuer Klartext + Farbe
  const [subtypesRegistry, setSubtypesRegistry] = useState<Record<string, SubtypeMeta>>({})

  // R103.29 (T-Day): "Zeitwertung"-TopTab ist aktiv wenn view in {overall, zeit}.
  const isZeitTopTab = view === 'overall' || view === 'zeit'

  const queryUrl = useMemo(() => {
    const params = new URLSearchParams({
      view,
      terrain: terrain.toLowerCase(),
      season: String(season),
    })
    if (view === 'klasse') params.set('class', klasse)
    if (view === 'zeit') params.set('bucket', bucket)
    // Einzelflug: KEIN class-Filter — klassen-übergreifend
    return `/api/public/wertung?${params.toString()}`
  }, [view, terrain, klasse, bucket, season])

  useEffect(() => {
    setLoading(true)
    setError(null)

    // Preview-Modus: ?preview=120 → Fake-Daten ohne API-Call
    if (previewCount) {
      if (view === 'einzelflug') {
        setData(generateFakeEinzelflug(previewCount))
      } else {
        setData(generateFakePilots(previewCount))
      }
      setLoading(false)
      return
    }

    // r19.55.216: stable error-codes statt lokalisierter Strings — die
    // UI-Ebene mappt die Codes auf t('wertung.loginRequiredError') etc.
    fetch(queryUrl)
      .then(r => {
        if (r.status === 401) throw new Error('LOGIN_REQUIRED')
        if (r.status === 423) throw new Error('RANKING_LOCKED')
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(j => {
        if (j.success) {
          setData(j.data)
          // R103.38: subtypesRegistry kommt jetzt mit jeder Response, Lookup-Map cachen
          if (j.subtypesRegistry && typeof j.subtypesRegistry === 'object') {
            setSubtypesRegistry(j.subtypesRegistry)
          }
        } else throw new Error(j.error || 'Unbekannter Fehler')
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }, [queryUrl, previewCount, view])

  return (
    <LoginContext.Provider value={isLoggedIn}>
    <PilotModalContext.Provider value={setModalRow}>
    <SubtypesRegistryContext.Provider value={subtypesRegistry}>
    <LenisProvider>
      {/* 2026-06-14 (Stephan-Spec): data-fgl-3d schaltet die High-End-Glas-
          Tiefe ein. Greift auf .award-chroma + .wertung-row-3d + .wertung-
          modal-glass + .stage-pill-3d + .award-chip — Layout (Parallelogramm,
          SailGP-Layout) bleibt eingefroren, nur Tiefe + Glow kommen dazu. */}
      <div
        data-award="true"
        data-fgl-3d="true"
        className="min-h-screen flex flex-col text-[var(--text)] relative overflow-x-clip"
        style={{ background: 'var(--bg-primary)' }}
      >
        <GliderBackdrop />
        <FglHeader />

        <main className="flex-1 max-w-[1400px] mx-auto w-full px-4 py-10 pt-[120px] relative z-10">

          {/* R103.33 (T-Day, Stephan-Spec): relative Wrapper damit die
              "Heute eingereicht"-Spalte rechts daneben absolut positioniert
              werden kann — von Oberkante der Ueberschrift bis Unterkante
              des Stats-Blocks (Piloten/Nationen/Valide Wertungsfluege). */}
          <div className="relative max-w-6xl mx-auto">

          {/* Rechte Spalte „Heute eingereicht" — absolute am rechten Rand der
              Wertungs-Breite, xl+ sichtbar (auf Tablet und kleiner ausgeblendet,
              dort uebernimmt der horizontale Marquee-Ticker im Header). Die
              Komponente begrenzt sich selbst auf 7 Eintraege + Scroll. */}
          {/* R106.4 (Stephan-Spec 2026-06-08): mobile auch sichtbar.
              xl+: absolute Sidebar wie bisher. Darunter: regulaerer Block. */}
          {/* R106.4 (2026-06-08 Stephan-Bug): xl:z-30 + isolation damit
              Vario-Dropdown + Sound-Toggle im Header der Spalte auf Desktop
              nicht von dahinter liegenden Wertungs-Headlines abgefangen
              werden. Mobile (kein xl:absolute) hatte das Problem nicht. */}
          <div
            className="relative w-full mb-6 xl:absolute xl:top-0 xl:right-0 xl:w-[220px] xl:mb-0 xl:z-30"
            style={{ isolation: 'isolate' }}
          >
            <TodaySubmittedColumn />
          </div>

          <Reveal>
            <div className="text-center mb-10">
              <div className="award-eyebrow mb-3" style={{ color: 'var(--accent)' }}>
                {t('wertung.eyebrowSeason', { season })}
              </div>
              {/* R106.3 (2026-06-07): Anton ultra-heavy + scroll-motion-trail Schatten */}
              <h1
                className="tracking-tight leading-none"
                style={{
                  fontFamily: 'var(--font-mega)',
                  fontWeight: 400,
                  fontSize: 'clamp(48px, 7.5vw, 96px)',
                  letterSpacing: '0.005em',
                  textTransform: 'uppercase',
                  textShadow: scrollShadow,
                  transition: 'text-shadow 80ms linear',
                }}
              >
                FGL <span className="award-grad-text">{t('wertung.pageTitleAccent')}</span>
              </h1>
            </div>
          </Reveal>

          {/* Master-Toggle FLATLAND vs SLOPE */}
          <Reveal>
            <div className="flex justify-center gap-2 mb-8">
              <TerrainPill
                active={terrain === 'FLATLAND'}
                icon={<Trees className="w-4 h-4" />}
                label={t('wertung.terrainFlatland')}
                onClick={() => setTerrain('FLATLAND')}
              />
              <TerrainPill
                active={terrain === 'SLOPE'}
                icon={<Mountain className="w-4 h-4" />}
                label={t('wertung.terrainSlope')}
                onClick={() => setTerrain('SLOPE')}
              />
            </div>
          </Reveal>

          {/* R103.29 (T-Day, Stephan-Spec): 3 UI-TopTabs in neuer Reihenfolge
              Einzelflug | Klassen | Zeitwertung. "Zeitwertung" gruppiert die
              alten Top-Tabs Overall + Zeit (Overall ist erste Sub-Option). */}
          <Reveal>
            {/* r19.55.86 (Stephan-Spec 2026-08-05): id-Anchor damit
                TodaySubmittedColumn per getBoundingClientRect pixel-genau
                seine Unterkante auf die Bottom-Edge dieser 2. Pillen-Reihe
                (BESTER EINZELFLUG / KLASSEN / ZEITWERTUNG) setzt. */}
            <div id="wertung-view-tabs-row" className="flex flex-wrap justify-center gap-2 mb-6">
              <ViewTab active={view === 'einzelflug'} icon={<Maximize2 className="w-4 h-4" />} label={t('wertung.viewSingleBest')} onClick={() => setView('einzelflug')} />
              <ViewTab active={view === 'klasse'} icon={<Layers className="w-4 h-4" />} label={t('wertung.viewClasses')} onClick={() => setView('klasse')} />
              <ViewTab
                active={isZeitTopTab}
                icon={<Clock className="w-4 h-4" />}
                label={t('wertung.viewTime')}
                onClick={() => { if (!isZeitTopTab) setView('overall') }}
              />
            </div>
          </Reveal>

          {/* Sub-Tabs: Klassen unveraendert (Club|Standard|Open), Zeitwertung
              jetzt mit Overall + 5 Buckets. */}
          {view === 'klasse' && (
            <Reveal>
              <div className="flex justify-center gap-2 mb-6">
                {(['CLUBCLASS', 'STANDARD', 'OPEN'] as Klasse[]).map(k => (
                  <SubPill
                    key={k}
                    active={klasse === k}
                    label={KLASSE_LABEL[k]}
                    onClick={() => setKlasse(k)}
                  />
                ))}
              </div>
            </Reveal>
          )}

          {isZeitTopTab && (
            <Reveal>
              <div className="flex justify-center flex-wrap gap-2 mb-6">
                {/* Overall als erste Sub-Option (= view 'overall' im Backend) */}
                <SubPill
                  active={view === 'overall'}
                  label={t('wertung.viewOverall')}
                  onClick={() => setView('overall')}
                />
                {(['30min', '1h', '2h', '3h', '3plus'] as Bucket[]).map(b => {
                  const isActive = view === 'zeit' && bucket === b
                  return (
                    <SubPill
                      key={b}
                      active={isActive}
                      label={t(`wertung.bucket.${b}`)}
                      style={{
                        borderColor: isActive ? BUCKET_COLOR[b] : undefined,
                        color: isActive ? BUCKET_COLOR[b] : undefined,
                      }}
                      onClick={() => { setView('zeit'); setBucket(b) }}
                    />
                  )
                })}
              </div>
            </Reveal>
          )}

          {/* Stats-Header — 4 Tiles (Piloten / Nationen / Flaggen / Valide)
              r19.55.84 (Stephan-Spec 2026-08-05): Jetzt auch fuer "Bester
              Einzelflug"-View sichtbar. Aggregate werden fuer diese View
              client-seitig aus flights[] abgeleitet (unique pilot.id +
              gruppiert nach countryCode).
              r19.55.86 (Stephan-Spec 2026-08-05): Flaggen als eigene 4. Spalte
              (statt unter "Nationen"-Zahl gruppiert). Nationen-Tile zeigt nur
              noch die Zahl, das FlagsTile daneben zeigt die Fahnen-Pills. */}
          {data && (() => {
            let statPilots = 0
            let statFlights = 0
            let statNations: { iso: string; pilotCount: number }[] = []
            if ('rows' in data) {
              statPilots = data.totalPilots
              statFlights = data.totalFlightsInPool
              statNations = data.nations || []
            } else if ('flights' in data) {
              // Einzelflug-View: aus flights[] ableiten
              const seenPilots = new Set<string>()
              const nationMap = new Map<string, Set<string>>()
              for (const f of data.flights) {
                const pid = f.pilot?.userId
                if (pid) seenPilots.add(pid)
                const iso = f.pilot?.countryCode
                if (iso && pid) {
                  const isoLower = iso.toLowerCase()
                  if (!nationMap.has(isoLower)) nationMap.set(isoLower, new Set())
                  nationMap.get(isoLower)!.add(pid)
                }
              }
              statPilots = seenPilots.size
              statFlights = data.totalFlights ?? data.flights.length
              statNations = Array.from(nationMap.entries())
                .map(([iso, pilots]) => ({ iso, pilotCount: pilots.size }))
                .sort((a, b) => b.pilotCount - a.pilotCount)
            }
            return (
              <Reveal>
                <div className="flex flex-col items-center mb-8">
                  <div className="flex justify-center items-start gap-8 sm:gap-12 flex-wrap">
                    <StatTile label={t('wertung.statPilots')} value={statPilots} />
                    <StatTile label={t('wertung.statNations')} value={statNations.length} />
                    <FlagsTile nations={statNations} />
                    <StatTile label={t('wertung.statValidFlights')} value={statFlights} />
                  </div>
                </div>
              </Reveal>
            )
          })()}

          </div>
          {/* /R103.33 relativer Wrapper fuer Tagesticker-Spalte */}

          {loading && (
            <div className="text-center py-20">
              <div className="inline-block w-8 h-8 rounded-full border-2 border-[var(--accent)] border-t-transparent animate-spin" />
              <div className="mt-4 text-[var(--muted)] text-sm">{t('wertung.computing')}</div>
            </div>
          )}

          {error && (
            <Reveal>
              <div className="max-w-xl mx-auto rounded-3xl p-10 border border-[var(--accent)]/30 bg-[var(--bg-card)] text-center"
                   style={{
                     boxShadow: '0 0 60px rgba(242,201,78,.15), inset 0 0 30px rgba(242,201,78,.04)',
                   }}>
                <Trophy className="w-12 h-12 mx-auto mb-4 text-[var(--accent)]" />
                {error === 'LOGIN_REQUIRED' ? (
                  <>
                    <div className="text-2xl font-black mb-3">{t('wertung.loginRequiredTitle')}</div>
                    <p className="text-[var(--muted)] text-sm leading-relaxed mb-6">
                      {t('wertung.loginRequiredBody')}
                    </p>
                    <div className="flex gap-3 justify-center flex-wrap">
                      <Link
                        href="/login"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-black uppercase tracking-widest text-sm bg-[var(--accent)] text-[var(--award-ink)] shadow-[0_0_30px_rgba(242,201,78,0.4)] hover:brightness-110 transition-all"
                      >
                        {t('wertung.loginButton')}
                      </Link>
                      <Link
                        href="/register"
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold uppercase tracking-widest text-sm border border-[var(--accent)]/40 text-[var(--accent)] hover:border-[var(--accent)] hover:bg-[var(--accent)]/10 transition-all"
                      >
                        {t('wertung.registerButton')}
                      </Link>
                    </div>
                  </>
                ) : error === 'RANKING_LOCKED' ? (
                  <>
                    <div className="text-2xl font-black mb-3">{t('wertung.seasonPrepTitle')}</div>
                    <p className="text-[var(--muted)] text-sm leading-relaxed">
                      {t('wertung.seasonPrepBody')}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="text-xl font-black mb-2 text-red-400">{error}</div>
                    <p className="text-[var(--muted)] text-sm">
                      {t('wertung.loadErrorBody')}
                    </p>
                  </>
                )}
              </div>
            </Reveal>
          )}

          {/* R100.54 (2026-05-29): Saison-Rekord-Balken oberhalb der Wertungs-Tabelle.
              Zeigt Rekord 2024 + 2025 + aktueller Bestwert für die jeweilige
              Kategorie. category-Mapping passt zu SeasonRecord.category-Werten. */}
          {!loading && !error && data && (() => {
            let category: string | null = null
            let title = ''
            const terrainParam = terrain === 'FLATLAND' ? 'flatland' as const : 'slope' as const
            const terrainLabel = terrain === 'FLATLAND' ? t('wertung.terrainFlatland') : t('wertung.terrainSlope')
            if (view === 'overall') {
              category = 'overall'
              title = t('wertung.recordTitleOverall', { terrainLabel })
            } else if (view === 'klasse') {
              category = klasse === 'CLUBCLASS' ? 'club' : klasse === 'STANDARD' ? 'standard' : 'open'
              const klasseLabel = klasse === 'CLUBCLASS' ? t('wertung.classClub') : klasse === 'STANDARD' ? t('wertung.classStandard') : t('wertung.classOpen')
              title = t('wertung.recordTitleClass', { klasseLabel, terrainLabel })
            } else if (view === 'zeit') {
              const bucketCat: Record<string, string> = { '30min': '30', '1h': '1', '2h': '2', '3h': '3', '3plus': '3+' }
              category = bucketCat[bucket] ?? null
              const bucketLabel = t(`wertung.bucketLong.${bucket}`)
              title = t('wertung.recordTitleTime', { bucketLabel, terrainLabel })
            } else if (view === 'einzelflug') {
              category = 'distance'
              title = t('wertung.recordTitleDistance', { terrainLabel })
            }
            if (!category) return null
            // R102.7 (Stephan-Spec 2026-06-01): RecordChaseCard mit Champion-
            // Foto + Klub + Fortschrittsbalken zwischen 2024- und 2025-Rekord.
            // Top-1-Pilot wird aus der bereits geladenen Wertungs-Top-Liste
            // genommen (kein zusätzlicher Fetch).
            let currentValue: number | null = null
            let currentLeader: { name: string; avatarUrl?: string | null; countryCode?: string | null; clubName?: string | null; slug?: string | null; isPublic?: boolean } | null = null
            const currentUnit: 'km' | 'pts' = view === 'einzelflug' ? 'km' : 'pts'
            if ('rows' in data && data.rows.length > 0) {
              const top = data.rows[0]
              currentValue = top.scoreKm
              currentLeader = {
                name: top.pilot.name,
                avatarUrl: top.pilot.avatarUrl ?? null,
                countryCode: top.pilot.countryCode ?? null,
                clubName: (top.pilot as any).clubName ?? null,
                slug: top.pilot.slug ?? null,
                isPublic: !!top.pilot.isPublic,
              }
            } else if ('flights' in data && data.flights.length > 0) {
              const f0 = data.flights[0] as any
              currentValue = f0.distanceKm ?? f0.flight?.distanceKm ?? null
              const pilot0 = f0.pilot || { name: f0.pilotName }
              currentLeader = {
                name: pilot0?.name || f0.pilotName || '—',
                avatarUrl: pilot0?.avatarUrl ?? null,
                countryCode: pilot0?.countryCode ?? null,
                clubName: pilot0?.clubName ?? null,
                slug: pilot0?.slug ?? null,
                isPublic: !!pilot0?.isPublic,
              }
            }

            // Title für die Card (oberhalb der Daten)
            const champTitle = (() => {
              const terrainShort = (terrainParam === 'flatland' ? t('wertung.terrainFlatland') : t('wertung.terrainSlope')).toUpperCase()
              if (view === 'einzelflug') return t('wertung.recordChampionDistance', { terrain: terrainShort })
              if (view === 'overall') return t('wertung.recordChampionOverall', { terrain: terrainShort })
              if (view === 'klasse') {
                const kl = klasse === 'CLUBCLASS' ? t('wertung.classClub').toUpperCase() : klasse === 'STANDARD' ? t('wertung.classStandard').toUpperCase() : t('wertung.classOpen').toUpperCase()
                return t('wertung.recordChampionClass', { klasse: kl, terrain: terrainShort })
              }
              if (view === 'zeit') {
                const bucketLabel = t(`wertung.bucket.${bucket}`)
                return t('wertung.recordChampionTime', { bucketLabel, terrain: terrainShort })
              }
              return title
            })()
            const sourceLabel = `${terrainParam}/${view === 'zeit' ? bucket : view}${view === 'klasse' ? `/${klasse}` : ''}`

            return (
              <div className="max-w-6xl mx-auto mb-6">
                <RecordChaseCard
                  category={category}
                  terrain={terrainParam}
                  title={champTitle}
                  sourceLabel={sourceLabel}
                  currentValue={currentValue}
                  currentLeader={currentLeader}
                  currentUnit={currentUnit}
                />
              </div>
            )
          })()}

          {/* R106.3 (Stephan-Spec 2026-06-07): Detail-Uebersicht Zeitwertungen pro
              Stage zwischen RecordChase und Top-10 (vor Platz 1). Auch in
              Einzelflug-View sichtbar (Stephan-Spec 2026-06-07 spaeter). */}
          <div className="max-w-6xl mx-auto mb-6">
            <BucketsAccordionSection terrain={terrain} initialClass={view === 'klasse' ? klasse : null} />
          </div>

          {/* R102.7.2 (Stephan-Wunsch 2026-06-01): „alle erklärungen können weg"
              — Wertungs-Regel-Box auf /wertung entfernt. Volltext steht
              weiterhin in der Wettbewerbsordnung (Link in FAQ + Footer). */}

          {/* R100.52.1 (2026-05-29): Type-Guards 'rows' in data / 'flights' in data —
              verhindert Crash bei View-Switch wenn data noch vom alten View ist
              und ein Re-Render läuft bevor useEffect den neuen Fetch ausgelöst hat. */}
          {!loading && !error && data && 'rows' in data && (view === 'overall' || view === 'klasse' || view === 'zeit') && (
            <PilotList data={data} view={view} currentBucket={view === 'zeit' ? bucket : undefined} terrain={terrain} klasse={view === 'klasse' ? klasse : null} />
          )}

          {!loading && !error && data && 'flights' in data && view === 'einzelflug' && (
            <EinzelflugList data={data} />
          )}

        </main>

        {/* R106.3 (2026-06-07): FglFooter raus — root layout rendert ihn bereits global */}
      </div>

      {/*
        Rank-Glow-System (Wertungsflug-Button-Look):
        - Platz 1: rotierende Gold-Aura (8s/360°), starke Strahlung
        - Platz 2: rotierende Silber-Aura
        - Platz 3: rotierende Bronze-Aura
        - Platz 4-10: dezenter statischer Glow, abnehmend
        - Platz 11+: kein Effekt

        Technik: ::before mit conic-gradient + inset:-6px (Halo außerhalb) +
        blur. ähnlich dem Donor-Spin, aber breiter und blühender. Pulse-Glow
        über box-shadow auf dem Element selbst.
      */}
      <style jsx global>{`
        /* @property ermöglicht echte Conic-Rotation ohne Pseudo-Tricks.
           Chrome/Safari/Edge: nativ. Firefox 128+ auch. Älter: kein Spin
           aber statischer Goldring bleibt sichtbar. */
        @property --fgl-ang {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes fgl-rank-ang-spin {
          to { --fgl-ang: 360deg; }
        }

        @keyframes fgl-rank-pulse-glow-gold {
          0%, 100% { box-shadow: 0 0 30px rgba(242,201,78,.40); }
          50%      { box-shadow: 0 0 56px rgba(242,201,78,.75); }
        }
        @keyframes fgl-rank-pulse-glow-silver {
          0%, 100% { box-shadow: 0 0 28px rgba(225,225,235,.35); }
          50%      { box-shadow: 0 0 50px rgba(225,225,235,.60); }
        }
        @keyframes fgl-rank-pulse-glow-bronze {
          0%, 100% { box-shadow: 0 0 28px rgba(205,127,50,.35); }
          50%      { box-shadow: 0 0 50px rgba(205,127,50,.60); }
        }

        /* Card mit rotierendem Conic-Border via background-clip:
           - 2 background layers: inner (var(--bg-card), padding-box) +
             outer conic (border-box).
           - Transparenter 2px Border lässt outer conic sichtbar.
           - animation rotiert --fgl-ang → conic dreht sich. */
        /* R100.54 (2026-05-29): Asymmetrische, phasenversetzte Animations-Speeds.
           Gold/Silber/Bronze drehen MIT unterschiedlicher Geschwindigkeit UND
           unterschiedlicher Pulse-Frequenz, plus negative animation-delay damit
           sie nicht alle gleichzeitig im selben Phasenpunkt starten.
           Performant: nur transform + opacity + box-shadow (GPU-beschleunigt). */
        .fgl-rank-spin-gold,
        .fgl-rank-spin-silver,
        .fgl-rank-spin-bronze {
          isolation: isolate;
          border: 3px solid transparent !important;
          background-clip: padding-box, border-box;
          background-origin: padding-box, border-box;
          will-change: box-shadow;
        }
        .fgl-rank-spin-gold {
          background-image:
            linear-gradient(var(--bg-card), var(--bg-card)),
            conic-gradient(from var(--fgl-ang),
              rgba(242,201,78,.25) 0deg,
              rgba(242,201,78,.55) 90deg,
              rgba(255,225,120,1)  170deg,
              rgba(255,255,200,1)  200deg,
              rgba(255,225,120,1)  230deg,
              rgba(242,201,78,.55) 310deg,
              rgba(242,201,78,.25) 360deg);
          /* Gold dreht am schnellsten (7s) und pulst zügig (3.2s, kein Delay) */
          animation: fgl-rank-ang-spin 7s linear infinite,
                     fgl-rank-pulse-glow-gold 3.2s ease-in-out infinite 0s;
        }
        .fgl-rank-spin-silver {
          background-image:
            linear-gradient(var(--bg-card), var(--bg-card)),
            conic-gradient(from var(--fgl-ang),
              rgba(225,225,235,.25) 0deg,
              rgba(225,225,235,.55) 90deg,
              rgba(255,255,255,1)   170deg,
              rgba(255,255,255,1)   200deg,
              rgba(255,255,255,.95) 230deg,
              rgba(225,225,235,.55) 310deg,
              rgba(225,225,235,.25) 360deg);
          /* Silber dreht langsamer (9.5s) UND in entgegengesetzter Richtung,
             pulst auf eigener Frequenz (4.1s) mit -0.7s Phasen-Offset →
             nie synchron mit Gold. */
          animation: fgl-rank-ang-spin 9.5s linear infinite reverse,
                     fgl-rank-pulse-glow-silver 4.1s ease-in-out infinite -0.7s;
        }
        .fgl-rank-spin-bronze {
          background-image:
            linear-gradient(var(--bg-card), var(--bg-card)),
            conic-gradient(from var(--fgl-ang),
              rgba(205,127,50,.25)  0deg,
              rgba(205,127,50,.55)  90deg,
              rgba(255,170,90,1)    170deg,
              rgba(255,200,140,1)   200deg,
              rgba(255,170,90,.95)  230deg,
              rgba(205,127,50,.55)  310deg,
              rgba(205,127,50,.25)  360deg);
          /* Bronze dreht am langsamsten (11s), pulst am ruhigsten (5.3s) mit
             -1.6s Offset. Asymmetrisch zu Gold + Silber. */
          animation: fgl-rank-ang-spin 11s linear infinite,
                     fgl-rank-pulse-glow-bronze 5.3s ease-in-out infinite -1.6s;
        }

        /* Plätze 4-10: dezenter statischer Gold-Halo, abnehmend */
        .fgl-rank-glow-fade-4  { box-shadow: 0 0 26px rgba(242,201,78,.35); }
        .fgl-rank-glow-fade-5  { box-shadow: 0 0 22px rgba(242,201,78,.30); }
        .fgl-rank-glow-fade-6  { box-shadow: 0 0 18px rgba(242,201,78,.25); }
        .fgl-rank-glow-fade-7  { box-shadow: 0 0 15px rgba(242,201,78,.20); }
        .fgl-rank-glow-fade-8  { box-shadow: 0 0 12px rgba(242,201,78,.16); }
        .fgl-rank-glow-fade-9  { box-shadow: 0 0 10px rgba(242,201,78,.12); }
        .fgl-rank-glow-fade-10 { box-shadow: 0 0  8px rgba(242,201,78,.09); }

        /* Block-Glow für Top 20/30/40/50 Spalten — abnehmend gold */
        .fgl-block-glow-1 { box-shadow: 0 0 24px rgba(242,201,78,.20), inset 0 0 14px rgba(242,201,78,.04); }
        .fgl-block-glow-2 { box-shadow: 0 0 18px rgba(242,201,78,.14), inset 0 0 10px rgba(242,201,78,.03); }
        .fgl-block-glow-3 { box-shadow: 0 0 12px rgba(242,201,78,.10), inset 0 0  8px rgba(242,201,78,.02); }
        .fgl-block-glow-4 { box-shadow: 0 0  8px rgba(242,201,78,.06); }

        /* TOP 3 Thermik-Bubbles — aufsteigende Glühpunkte (Gold/Silber/Bronze).
           Liegen absolute im Card-Background, blockieren keinen Text. */
        @keyframes fgl-bubble-rise {
          0%   { transform: translateY(20%) scale(.6); opacity: 0; }
          15%  { opacity: var(--fgl-bubble-peak, .75); }
          85%  { opacity: var(--fgl-bubble-peak, .65); }
          100% { transform: translateY(-160%) scale(1.0); opacity: 0; }
        }
        .fgl-bubble {
          position: absolute;
          bottom: 0;
          width: 26px; height: 26px;
          border-radius: 50%;
          filter: blur(8px);
          animation: fgl-bubble-rise 7s ease-in-out infinite;
          will-change: transform, opacity;
        }
        .fgl-bubble-gold {
          background: radial-gradient(circle, rgba(255,225,120,1) 0%, rgba(242,201,78,.7) 40%, rgba(242,201,78,0) 75%);
          --fgl-bubble-peak: .85;
        }
        .fgl-bubble-silver {
          background: radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(225,225,235,.7) 40%, rgba(225,225,235,0) 75%);
          --fgl-bubble-peak: .70;
        }
        .fgl-bubble-bronze {
          background: radial-gradient(circle, rgba(255,180,100,1) 0%, rgba(205,127,50,.7) 40%, rgba(205,127,50,0) 75%);
          --fgl-bubble-peak: .75;
        }
        /* 9 versetzte Positionen + delays + Speeds */
        .fgl-bubble-i0 { left:  6%; animation-delay: 0s;    animation-duration: 7s;  }
        .fgl-bubble-i1 { left: 18%; animation-delay: 1.2s;  animation-duration: 6s;  }
        .fgl-bubble-i2 { left: 30%; animation-delay: 3.4s;  animation-duration: 8s;  }
        .fgl-bubble-i3 { left: 42%; animation-delay: 2.0s;  animation-duration: 7.5s;}
        .fgl-bubble-i4 { left: 54%; animation-delay: 4.5s;  animation-duration: 6.5s;}
        .fgl-bubble-i5 { left: 66%; animation-delay: 0.6s;  animation-duration: 8.5s;}
        .fgl-bubble-i6 { left: 78%; animation-delay: 3.8s;  animation-duration: 6s;  }
        .fgl-bubble-i7 { left: 88%; animation-delay: 1.8s;  animation-duration: 7s;  }
        .fgl-bubble-i8 { left: 94%; animation-delay: 5.0s;  animation-duration: 8s;  }
        /* Auf grösseren Cards (TOP 1) zusätzlich grössere Bubbles */
        .fgl-bubble-i0, .fgl-bubble-i4, .fgl-bubble-i8 { width: 34px; height: 34px; filter: blur(10px); }

        @keyframes fgl-modal-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>

      {/* Pilot-Info-Modal — wird von CompactPilotRow geöffnet (TOP 11+) */}
      <PilotInfoModal row={modalRow} onClose={() => setModalRow(null)} view={view} />
    </LenisProvider>
    </SubtypesRegistryContext.Provider>
    </PilotModalContext.Provider>
    </LoginContext.Provider>
  )
}

// ════════════════════════════════════════════════════════════════
//   UI-Bausteine
// ════════════════════════════════════════════════════════════════

// 2026-06-15 (Stephan-Spec): drei Pill-Komponenten auf Glas-Design umgestellt
// (.wertung-tab-pill + data-state). Layout (Padding, Font) bleibt, Aussehen
// kommt jetzt aus globals.css mit Bevel + Multi-Shadow + Gold-Glow im Aktiv.
function TerrainPill({
  active, icon, label, onClick,
}: { active: boolean; icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      data-state={active ? 'active' : 'inactive'}
      className="wertung-tab-pill wertung-tab-pill-lg inline-flex items-center gap-2 px-6 py-3 rounded-full font-black uppercase tracking-widest text-sm"
    >
      {icon}
      {label}
    </button>
  )
}

function ViewTab({
  active, icon, label, onClick,
}: { active: boolean; icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      data-state={active ? 'active' : 'inactive'}
      className="wertung-tab-pill inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-xs uppercase tracking-widest"
    >
      {icon}
      {label}
    </button>
  )
}

function SubPill({
  active, label, onClick, style,
}: { active: boolean; label: string; onClick: () => void; style?: React.CSSProperties }) {
  return (
    <button
      onClick={onClick}
      style={style}
      data-state={active ? 'active' : 'inactive'}
      className="wertung-tab-pill px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-widest"
    >
      {label}
    </button>
  )
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-center">
      <div className="text-3xl font-black tabular-nums text-[var(--text)]">
        <CountUp value={value} duration={1.2} />
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--muted)] mt-1">
        {label}
      </div>
    </div>
  )
}

/**
 * FlagsTile — r19.55.86 (Stephan-Spec 2026-08-05)
 * Eigene 4. Spalte im Stats-Header (Piloten / Nationen / Flaggen / Valide).
 * Fahnen-Pills mit Piloten-Zaehler + FGL-Sprechblase, ohne obere Riesenzahl.
 */
/** r19.55.89 (Stephan-Spec 2026-08-05): ISO-2 → Nation-Vollname via
 *  Intl.DisplayNames — deutsch, browser-eingebaut, kein extra Bundle.
 *  Fallback = ISO-Code uppercase wenn Intl.DisplayNames nicht supported.
 */
function nationName(iso: string): string {
  try {
    const dn = new Intl.DisplayNames(['de'], { type: 'region' })
    return dn.of(iso.toUpperCase()) || iso.toUpperCase()
  } catch {
    return iso.toUpperCase()
  }
}

function FlagsTile({
  nations,
}: {
  nations: { iso: string; pilotCount: number }[]
}) {
  const { t } = useTranslation()
  if (nations.length === 0) return null
  // r19.55.89 (Stephan-Spec 2026-08-05): Reihenfolge umgekehrt — Flaggen-Pillen
  // OBEN (an der Stelle wo Nachbar-Tiles die 3xl-Zahl haben), Label "FLAGGEN"
  // DARUNTER (analog "PILOTEN"/"NATIONEN"/"VALIDE WERTUNGSFLÜGE"). Min-height
  // haelt die Baseline mit den 3xl-Zahlen der Nachbar-Tiles.
  return (
    <div className="text-center">
      <div
        className="flex justify-center flex-nowrap gap-1 items-center"
        style={{ minHeight: 36 /* ~ text-3xl leading-none */ }}
      >
        {nations.slice(0, 18).map(n => (
          <span
            key={n.iso}
            data-tip={`${nationName(n.iso)} (${n.iso.toUpperCase()}) · ${n.pilotCount === 1 ? t('wertung.pilotsPerNationOne') : t('wertung.pilotsPerNationMany', { count: n.pilotCount })}`}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/[0.04] border border-[var(--border)] shrink-0 whitespace-nowrap"
          >
            <img
              src={flagSrc(n.iso)}
              alt={n.iso.toUpperCase()}
              className="w-[16px] h-[11px] rounded-[2px] object-cover"
              loading="lazy"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
            />
            <span className="text-[10px] tabular-nums font-bold text-[var(--muted)]">{n.pilotCount}</span>
          </span>
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--muted)] mt-1">
        {t('wertung.flagsLabel')}
      </div>
    </div>
  )
}

function StatTileWithFlags({
  label, nations,
}: {
  label: string
  nations: { iso: string; pilotCount: number }[]
}) {
  // r19.55.84 (Stephan-Spec 2026-08-05): Analog zum Schnellueberblick-Header
  // (Zeile 2782) jetzt Flagge + Piloten-Anzahl sichtbar pro Nation, statt nur
  // Flagge mit HTML-title-Tooltip. FGL-Sprechblase per data-tip.
  // r19.55.86: NICHT MEHR REFERENZIERT — Nationen-Zahl + Flaggen sind jetzt
  // 2 getrennte Tiles (StatTile + FlagsTile). Function bleibt fuer eventuelle
  // Wiederverwendung.
  return (
    <div className="text-center">
      <div className="text-3xl font-black tabular-nums text-[var(--text)]">
        <CountUp value={nations.length} duration={1.2} />
      </div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--muted)] mt-1">
        {label}
      </div>
      {nations.length > 0 && (
        <div className="flex justify-center flex-wrap gap-1 mt-2 max-w-[240px]">
          {nations.slice(0, 12).map(n => (
            <span
              key={n.iso}
              data-tip={`${n.iso.toUpperCase()} · ${n.pilotCount} ${n.pilotCount === 1 ? 'Pilot' : 'Piloten'}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/[0.04] border border-[var(--border)]"
            >
              <img
                src={flagSrc(n.iso)}
                alt={n.iso.toUpperCase()}
                className="w-[16px] h-[11px] rounded-[2px] object-cover"
                loading="lazy"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
              />
              <span className="text-[10px] tabular-nums font-bold text-[var(--muted)]">{n.pilotCount}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Wertungsregel-Info-Box — erklärt kurz die Logik der aktuellen Ansicht.
 * Sitzt über der Pilot-Liste, lädt nicht in den Wertungs-Block.
 */
function WertungsRuleBox({
  view, bucket, klasse,
}: {
  view: View
  bucket?: Bucket
  klasse?: Klasse
}) {
  // R100.55 (2026-05-30): Texte kommen jetzt aus der Single Source of Truth
  // `@/lib/wertungs-regeln` — sie spiegeln die offizielle Wettbewerbsordnung
  // und werden auch im FAQ, in Tooltips und in der KI-Wissensbasis verwendet.
  // Wenn sich die Wettbewerbsordnung ändert, wird nur dort angepasst.
  const regel = getWertungsRegelText(view as WertungsView, {
    bucket: bucket as WertungsBucket | undefined,
    klasse: klasse as WertungsKlasse | undefined,
  })

  return (
    <Reveal>
      <div className="max-w-3xl mx-auto mb-6 rounded-2xl border border-[var(--accent)]/25 bg-[var(--accent)]/5 px-4 py-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-[var(--accent)] mb-1">
            {regel.title}
          </div>
          <p className="text-[12px] sm:text-[13px] leading-relaxed text-[var(--muted)]">
            {regel.body}
          </p>
          {regel.extras && regel.extras.length > 0 && (
            <ul className="mt-2 pl-4 list-disc text-[11px] sm:text-[12px] leading-relaxed text-[var(--muted)]">
              {regel.extras.map(x => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          <a
            href="/wettbewerbsordnung"
            className="mt-2 inline-block text-[10px] font-semibold text-[var(--accent)] hover:underline"
          >
            Vollständige Wettbewerbsordnung ansehen →
          </a>
        </div>
      </div>
    </Reveal>
  )
}

function PilotList({
  data, view, currentBucket, terrain, klasse,
}: {
  data: BaseResult
  view: View
  currentBucket?: Bucket
  terrain: Terrain
  klasse: Klasse | null
}) {
  const { t } = useTranslation()
  // R100.52 (2026-05-29): Single-Open-Verhalten — wenn eine TOP-10-Card
  // geöffnet wird, schließen sich die anderen elegant (framer-motion).
  // State hier hochgezogen damit die Cards koordinieren können.
  const [openRowId, setOpenRowId] = useState<string | null>(null)

  if (data.rows.length === 0) {
    return (
      <div className="text-center py-16 text-[var(--muted)]">
        <Trophy className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <div className="text-sm">{t('wertung.noPilotsEmpty')}</div>
      </div>
    )
  }

  // ── 3-Stufen-Layout (Stephan-Skizze 2026-05-29) ────────────────────
  //   1) TOP 10        — einspaltig, Hero-Cards (Rotation Gold/Silber/Bronze)
  //   2) TOP 11–50     — 4 Spalten × 10 Piloten, abnehmender Block-Glow
  //   3) TOP 51+       — kompakte Multi-Column-Liste
  // Mobile/Tablet: Block & Rest sind Accordion (default zu).
  // PC (md+):    alles sichtbar.
  const top10  = data.rows.slice(0, 10)
  const block  = data.rows.slice(10, 50)
  const rest   = data.rows.slice(50)

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* ── TOP 10 ─────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <SectionHeading label="Top 10" />
        {top10.map(row => (
          <PilotRowCard
            key={row.pilot.userId}
            row={row}
            view={view}
            currentBucket={currentBucket}
            isExpanded={openRowId === row.pilot.userId}
            onToggle={() =>
              setOpenRowId(prev => (prev === row.pilot.userId ? null : row.pilot.userId))
            }
          />
        ))}
      </section>

      {/* ── TOP 11–50 (4 Spalten) ──────────────────────────────────── */}
      {block.length > 0 && (
        <MidBlockSection rows={block} />
      )}

      {/* ── TOP 51+ ────────────────────────────────────────────────── */}
      {rest.length > 0 && (
        <CompactRestSection rows={rest} />
      )}
    </div>
  )
}

function SectionHeading({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 px-1">
      <span className="text-[10px] uppercase tracking-[.16em] font-black text-[var(--muted)]">
        {label}
      </span>
      <div className="flex-1 h-px bg-[var(--border)]" />
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
//   TOP 11–50 — 4 Spalten × 10 Piloten, abnehmender Block-Glow
// ════════════════════════════════════════════════════════════════

function MidBlockSection({ rows }: { rows: PilotRow[] }) {
  const [open, setOpen] = useState(true) // PC default offen — Mobile via CSS

  // 4 Buckets: 11-20, 21-30, 31-40, 41-50
  const buckets: { label: string; rows: PilotRow[]; glow: 1 | 2 | 3 | 4 }[] = [
    { label: 'Top 20', rows: rows.slice(0,  10), glow: 1 },
    { label: 'Top 30', rows: rows.slice(10, 20), glow: 2 },
    { label: 'Top 40', rows: rows.slice(20, 30), glow: 3 },
    { label: 'Top 50', rows: rows.slice(30, 40), glow: 4 },
  ]

  return (
    <section>
      {/* Mobile Accordion-Toggle, ab md immer offen */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-2 py-2 md:cursor-default"
        aria-expanded={open}
      >
        <span className="text-[10px] uppercase tracking-[.16em] font-black text-[var(--muted)]">
          Top 11 – 50
        </span>
        <div className="flex-1 h-px bg-[var(--border)]" />
        <ChevronDown
          className={`w-4 h-4 text-[var(--muted)] transition-transform md:hidden ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <div className={`${open ? 'block' : 'hidden'} md:block mt-3`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {buckets.map((b, i) =>
            b.rows.length > 0 ? (
              <BlockColumn key={i} label={b.label} rows={b.rows} glow={b.glow} />
            ) : null,
          )}
        </div>
      </div>
    </section>
  )
}

function BlockColumn({
  label, rows, glow,
}: {
  label: string
  rows: PilotRow[]
  glow: 1 | 2 | 3 | 4
}) {
  return (
    <div
      className={`rounded-2xl border bg-[var(--bg-card)] p-3 fgl-block-glow-${glow}`}
      style={{ borderColor: 'var(--border)' }}
    >
      <div className="text-[11px] uppercase tracking-[.14em] font-black mb-2 px-1"
           style={{ color: 'var(--accent)' }}>
        {label}
      </div>
      <div className="space-y-1">
        {rows.map(row => (
          <CompactPilotRow key={row.pilot.userId} row={row} />
        ))}
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════════
//   TOP 51+ — kompakte Multi-Column-Liste
// ════════════════════════════════════════════════════════════════

function CompactRestSection({ rows }: { rows: PilotRow[] }) {
  const [open, setOpen] = useState(true)
  return (
    <section>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-2 py-2 md:cursor-default"
        aria-expanded={open}
      >
        <span className="text-[10px] uppercase tracking-[.16em] font-black text-[var(--muted)]">
          Top 100 &amp; weitere · {rows.length} Piloten
        </span>
        <div className="flex-1 h-px bg-[var(--border)]" />
        <ChevronDown
          className={`w-4 h-4 text-[var(--muted)] transition-transform md:hidden ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <div className={`${open ? 'block' : 'hidden'} md:block mt-3`}>
        <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
            {rows.map(row => (
              <CompactPilotRow key={row.pilot.userId} row={row} mini />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

// ════════════════════════════════════════════════════════════════
//   CompactPilotRow — kleiner Pilot-Row für Block + Rest
// ════════════════════════════════════════════════════════════════

function CompactPilotRow({ row, mini }: { row: PilotRow; mini?: boolean }) {
  const { t } = useTranslation()
  const openModal = usePilotModal()
  // r19.55.91 (Stephan-Spec 2026-08-05): Anwaerter-Modus auch in den
  // TOP 11-50 Compact-Cards (nicht nur in den grossen Top-10 PilotRowCards).
  const isAnwaerter = row.meetsClassMin === false
  const anwaerterTip = isAnwaerter
    ? (row.missingFlights === 1 ? t('wertung.candidateTooltipOne', { name: row.pilot.name }) : t('wertung.candidateTooltipMany', { missing: row.missingFlights ?? 5, name: row.pilot.name }))
    : undefined
  return (
    <button
      type="button"
      onClick={() => openModal(row)}
      className="group w-full text-left block px-2 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors"
      style={{
        opacity: isAnwaerter ? 0.55 : 1,
        filter: isAnwaerter ? 'grayscale(0.55)' : undefined,
      }}
      data-tip={anwaerterTip}
    >
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-black text-[var(--muted)] tabular-nums w-5 text-right shrink-0">
          {row.rank}
        </span>
        <DonorAvatar
          src={row.pilot.avatarUrl}
          name={row.pilot.name}
          donorTier={row.pilot.donorTier}
          size={mini ? 20 : 22}
        />
        {/* Name + Flagge + Score auf zwei Zeilen damit Name immer voll sichtbar */}
        <div className="flex-1 min-w-0">
          <div
            className="truncate text-[12px] font-bold leading-tight text-[var(--text)] group-hover:text-[var(--accent)] transition-colors"
            title={row.pilot.name}
          >
            {row.pilot.name}
            {isAnwaerter && (
              <span className="ml-1 text-[8px] uppercase tracking-widest text-[var(--muted)] font-black">
                · {t('wertung.candidateLabel')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 leading-tight">
            <FlagIcon iso={row.pilot.countryCode} size={11} />
            <span className="text-[10px] font-bold text-[var(--accent)] tabular-nums">
              {fmtKm(row.scoreKm)}
            </span>
          </div>
        </div>
      </div>
    </button>
  )
}

// ════════════════════════════════════════════════════════════════
//   R103.10 (T-Day) — Bucket-Tabelle als Akkordeon
//   5-Spalten-Layout (30 min / 1 h / 2 h / 3 h / 3+ h), pro Spalte
//   sortierte Pilot-Liste nach erreichter Strecke IN dieser Stage.
//   Klassen-Filter intern (All/Club/Standard/Open).
//   Bei Klassen-Filter mit <5 Flügen: Pilot bleibt sichtbar, aber
//   ausgegraut mit motivierendem Tooltip.
// ════════════════════════════════════════════════════════════════

const BUCKET_ORDER: Bucket[] = ['30min', '1h', '2h', '3h', '3plus']
const BUCKET_HEADER: Record<Bucket, string> = {
  '30min': '30 MIN',
  '1h':    '1 H',
  '2h':    '2 H',
  '3h':    '3 H',
  '3plus': '3+ H',
}

interface BucketsApiPilot {
  rank: number
  pilot: PilotMeta
  scoreKm: number
  avgKmh: number | null
  flightId: string
  speedBatches: string[]
  classFlightCount: number
  meetsClassMin: boolean
  contributingFlights: FlightCard[]
  stageDistKms: number[]
}
interface BucketsApiColumn {
  bucket: Bucket
  label: string
  flightCount: number
  rows: BucketsApiPilot[]
}
interface BucketsApiResponse {
  success: boolean
  terrain: Terrain
  classFilter: Klasse | null
  data: {
    columns: BucketsApiColumn[]
    // Stephan-Wunsch 2026-06-25: Header-Aggregate
    totalPilots: number
    totalFlights: number
    totalScoringKm: number
    nations: { iso: string; pilotCount: number }[]
  }
}

function BucketsAccordionSection({
  terrain: parentTerrain,
  initialClass,
}: {
  terrain: Terrain
  initialClass: Klasse | null
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [classFilter, setClassFilter] = useState<Klasse | 'all'>(initialClass ?? 'all')
  // r19.46.2 (Stephan-Spec 2026-06-26): zusätzliche 5. Pille „Bester Einzelflug".
  // Eigener State damit Bucket-Tabelle nicht beeinflusst wird.
  const [raceView, setRaceView] = useState<'overall' | 'einzelflug' | Klasse>('overall')
  // r19.44.10 (2026-06-26, additiv nach Rollback): Terrain-Switch direkt im
  // Schnellüberblick. Default = Parent-Terrain (oben). Bei Parent-Wechsel
  // zieht das Akkordeon automatisch mit. User kann hier unten zusätzlich
  // schnell zwischen Flatland und Slope schalten, ohne die Top-10 zu beeinflussen.
  const [terrain, setTerrain] = useState<Terrain>(parentTerrain)
  useEffect(() => { setTerrain(parentTerrain) }, [parentTerrain])
  const [data, setData] = useState<BucketsApiResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)


  // R106.3 (Stephan-Spec 2026-06-07): Zeitraum-Filter
  type Period = 'today' | 'week' | 'year' | 'custom'
  const [period, setPeriod] = useState<Period>('year')
  const todayIso = () => new Date().toISOString().slice(0, 10)
  const [customFrom, setCustomFrom] = useState<string>(todayIso())
  const [customTo, setCustomTo] = useState<string>(todayIso())

  // R106.3-final (Stephan-Spec 2026-06-07): KW-Auswahlliste
  // Berechnet alle Kalenderwochen ab Jahresanfang 2024 bis aktuelle KW.
  const weekOptions = useMemo(() => {
    const options: Array<{ key: string; year: number; week: number; from: Date; to: Date; label: string }> = []
    const now = new Date()
    // ISO-Week aus Datum (Mo = 1. Tag)
    const startMonday = (d: Date) => {
      const x = new Date(d)
      const dow = (x.getUTCDay() + 6) % 7 // 0 = Mo
      x.setUTCDate(x.getUTCDate() - dow)
      x.setUTCHours(0, 0, 0, 0)
      return x
    }
    const isoWeek = (d: Date) => {
      const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
      const dayNum = (x.getUTCDay() + 6) % 7
      x.setUTCDate(x.getUTCDate() - dayNum + 3)
      const firstThursday = new Date(Date.UTC(x.getUTCFullYear(), 0, 4))
      const firstThursdayDay = (firstThursday.getUTCDay() + 6) % 7
      firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayDay + 3)
      const diff = x.getTime() - firstThursday.getTime()
      return { year: x.getUTCFullYear(), week: 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000)) }
    }
    const fmt = (d: Date) => `${d.getUTCDate().toString().padStart(2, '0')}.${(d.getUTCMonth() + 1).toString().padStart(2, '0')}.${d.getUTCFullYear()}`
    // Generiere ab 2024-01-01 vorwärts
    const startYear = 2024
    let cursor = startMonday(new Date(Date.UTC(startYear, 0, 4))) // 4. Jan ist immer in KW 1
    const todayMonday = startMonday(now)
    while (cursor <= todayMonday) {
      const sunday = new Date(cursor)
      sunday.setUTCDate(sunday.getUTCDate() + 6)
      const { year, week } = isoWeek(cursor)
      options.push({
        key: `${year}-${week.toString().padStart(2, '0')}`,
        year,
        week,
        from: new Date(cursor),
        to: new Date(sunday),
        label: `KW ${week} / ${year} · ${fmt(cursor)} – ${fmt(sunday)}`,
      })
      const next = new Date(cursor)
      next.setUTCDate(next.getUTCDate() + 7)
      cursor = next
    }
    return options.reverse() // neueste oben
  }, [])

  // Aktuelle Auswahl-KW (Default = aktuelle Woche)
  const [selectedWeekKey, setSelectedWeekKey] = useState<string>(() => {
    const now = new Date()
    const dow = (now.getDay() + 6) % 7
    const monday = new Date(now)
    monday.setDate(now.getDate() - dow)
    // ISO-Week
    const x = new Date(Date.UTC(monday.getFullYear(), monday.getMonth(), monday.getDate()))
    const dayNum = (x.getUTCDay() + 6) % 7
    x.setUTCDate(x.getUTCDate() - dayNum + 3)
    const firstThursday = new Date(Date.UTC(x.getUTCFullYear(), 0, 4))
    const firstThursdayDay = (firstThursday.getUTCDay() + 6) % 7
    firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayDay + 3)
    const week = 1 + Math.round((x.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000))
    return `${x.getUTCFullYear()}-${week.toString().padStart(2, '0')}`
  })

  // Wenn die parent-Page den klasse-Tab wechselt: Filter mitnehmen
  useEffect(() => {
    if (initialClass !== null) setClassFilter(initialClass)
  }, [initialClass])

  // Fetch wenn aufgeklappt + bei Terrain-/Filter-/Zeitraum-Wechsel
  useEffect(() => {
    if (!open) return
    let abort = false
    setLoading(true)
    setError(null)
    const cls = classFilter === 'all' ? 'all' : classFilter
    const t = terrain === 'FLATLAND' ? 'flatland' : 'slope'

    // Zeitraum-Params bauen
    let dateParams = ''
    if (period === 'today') {
      const d = todayIso()
      dateParams = `&from=${d}&to=${d}`
    } else if (period === 'week') {
      // Aus selectedWeekKey die KW aus weekOptions holen
      const sel = weekOptions.find(w => w.key === selectedWeekKey) || weekOptions[0]
      if (sel) {
        const fromIso = sel.from.toISOString().slice(0, 10)
        const toIso = sel.to.toISOString().slice(0, 10)
        dateParams = `&from=${fromIso}&to=${toIso}`
      }
    } else if (period === 'custom' && customFrom && customTo) {
      dateParams = `&from=${customFrom}&to=${customTo}`
    }
    // period === 'year' → kein from/to → Default (aktuelles Jahr) greift

    fetch(`/api/public/wertung/buckets?terrain=${t}&class=${cls}${dateParams}`)
      .then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(j?.error || `HTTP ${r.status}`)))
      .then(j => { if (!abort) setData(j as BucketsApiResponse) })
      .catch(e => { if (!abort) setError(typeof e === 'string' ? e : 'Fehler') })
      .finally(() => { if (!abort) setLoading(false) })
    return () => { abort = true }
  }, [open, terrain, classFilter, period, customFrom, customTo, selectedWeekKey, weekOptions])

  return (
    <section>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-2 py-2"
        aria-expanded={open}
      >
        <span className="text-[10px] uppercase tracking-[.16em] font-black text-[var(--muted)]">
          {t('wertung.bucketAccordionTitle')}
        </span>
        <div className="flex-1 h-px bg-[var(--border)]" />
        <ChevronDown className={`w-4 h-4 text-[var(--muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {/* R106.3 (Stephan-Spec 2026-06-07): Zeitraum-Filter — theme-aware
              identisch zum Klassen-Filter (--accent Gold als aktiv). */}
          <div className="flex items-center gap-1.5 px-1 flex-wrap">
            {(['today', 'week', 'year', 'custom'] as const).map(p => {
              const active = period === p
              const label = p === 'today' ? t('wertung.periodToday') : p === 'week' ? t('wertung.periodWeek') : p === 'year' ? t('wertung.periodYear') : t('wertung.periodCustom')
              return (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  data-state={active ? 'active' : 'inactive'}
                  className="wertung-tab-pill px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider"
                >
                  {label}
                </button>
              )
            })}
            {period === 'custom' && (
              <div className="flex items-center gap-1.5 ml-2">
                <input
                  type="date"
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  className="px-2 py-1 rounded text-[10px] bg-[var(--bg-card)] text-[var(--text)] border border-[var(--border)] font-mono"
                />
                <span className="text-[var(--muted)] text-[10px]">→</span>
                <input
                  type="date"
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  className="px-2 py-1 rounded text-[10px] bg-[var(--bg-card)] text-[var(--text)] border border-[var(--border)] font-mono"
                />
              </div>
            )}
            {period === 'week' && (
              <select
                value={selectedWeekKey}
                onChange={e => setSelectedWeekKey(e.target.value)}
                className="ml-2 px-2 py-1 rounded text-[10px] bg-[var(--bg-card)] text-[var(--text)] border border-[var(--border)] font-mono"
              >
                {weekOptions.map(w => (
                  <option key={w.key} value={w.key}>{w.label}</option>
                ))}
              </select>
            )}
          </div>

          {/* r19.44.10: Terrain-Switch + Klassen-Filter */}
          <div className="flex items-center gap-3 px-1 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              {(['FLATLAND', 'SLOPE'] as const).map(terr => {
                const active = terrain === terr
                const label = terr === 'FLATLAND' ? t('wertung.terrainFlatland') : t('wertung.terrainSlope')
                return (
                  <button
                    key={terr}
                    onClick={() => setTerrain(terr)}
                    data-state={active ? 'active' : 'inactive'}
                    className="wertung-tab-pill px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider"
                  >
                    {label}
                  </button>
                )
              })}
            </div>
            <div className="h-4 w-px bg-[var(--border)]" />
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* r19.46.2 (Stephan-Spec): „Bester Einzelflug" als 5. Pille.
                  Setzt nur raceView, classFilter (für Bucket-Tabelle) bleibt. */}
              {(['all', 'einzelflug', 'CLUBCLASS', 'STANDARD', 'OPEN'] as const).map(c => {
                const active = c === 'einzelflug'
                  ? raceView === 'einzelflug'
                  : (raceView !== 'einzelflug' && classFilter === c)
                const label =
                  c === 'all' ? t('wertung.viewOverall') :
                  c === 'einzelflug' ? t('wertung.viewSingleBest') :
                  c === 'CLUBCLASS' ? t('wertung.classClub') :
                  c === 'STANDARD' ? t('wertung.classStandard') : t('wertung.classOpen')
                return (
                  <button
                    key={c}
                    onClick={() => {
                      if (c === 'einzelflug') {
                        setRaceView('einzelflug')
                      } else {
                        setClassFilter(c)
                        setRaceView(c === 'all' ? 'overall' : c)
                      }
                    }}
                    data-state={active ? 'active' : 'inactive'}
                    className="wertung-tab-pill px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider"
                  >
                    {label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Inhalt */}
          {loading && (
            <div className="text-center py-8 text-[10px] text-[var(--muted)] uppercase tracking-wider">
              {t('wertung.loading')}
            </div>
          )}
          {error && (
            <div className="text-center py-8 text-[10px] text-red-400 uppercase tracking-wider">
              {error}
            </div>
          )}
          {data && !loading && (
            <>
              {/* Stephan-Wunsch 2026-06-25: Header-Stats über der Spalten-Tabelle —
                  Piloten, Flüge, Σ km der Zeitwertung über alles + Nationen-Flaggen. */}
              <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] px-4 py-3 mb-3">
                <div className="flex items-end gap-6 flex-wrap">
                  <div>
                    <div className="text-[9px] uppercase tracking-[.16em] font-black text-[var(--muted)] mb-0.5">{t('wertung.statPilots')}</div>
                    <div className="text-xl sm:text-2xl font-black tabular-nums text-[var(--accent)] leading-none">
                      {data.data.totalPilots.toLocaleString('de-DE')}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-[.16em] font-black text-[var(--muted)] mb-0.5">{t('wertung.statScoringFlights')}</div>
                    <div className="text-xl sm:text-2xl font-black tabular-nums text-[var(--accent)] leading-none">
                      {data.data.totalFlights.toLocaleString('de-DE')}
                    </div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-[.16em] font-black text-[var(--muted)] mb-0.5">{t('wertung.statScoringKm')}</div>
                    <div className="text-xl sm:text-2xl font-black tabular-nums text-[var(--accent)] leading-none">
                      {data.data.totalScoringKm.toLocaleString('de-DE', { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                  {data.data.nations.length > 0 && (
                    <div className="flex-1 min-w-[140px]">
                      <div className="text-[9px] uppercase tracking-[.16em] font-black text-[var(--muted)] mb-1">{t('wertung.statNations')}</div>
                      <div className="flex items-center gap-1 flex-wrap">
                        {data.data.nations.slice(0, 18).map(n => (
                          <span
                            key={n.iso}
                            data-tip={n.pilotCount === 1 ? t('wertung.pilotsPerNationOne') : t('wertung.pilotsPerNationMany', { count: n.pilotCount })}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/[0.04] border border-[var(--border)]"
                          >
                            <FlagIcon iso={n.iso} size={12} />
                            <span className="text-[9px] tabular-nums text-[var(--muted)] font-bold">{n.pilotCount}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* r19.45.4 (Stephan-Spec 2026-06-26): Race-Track-Hero über den
                  Bucket-Spalten — folgt den Klassen-Filter-Pillen des
                  Akkordeons (Overall / Bester Einzelflug / Club / Standard / Open).
                  r19.46.2: Top-30 + „Bester Einzelflug" als 5. Pille. */}
              {(() => {
                const headerWord =
                  raceView === 'einzelflug'    ? t('wertung.viewSingleBest') :
                  raceView === 'overall'       ? t('wertung.viewOverall') :
                  raceView === 'CLUBCLASS'     ? t('wertung.classRankingClub') :
                  raceView === 'STANDARD'      ? t('wertung.classRankingStandard') :
                                                 t('wertung.classRankingOpen')
                const terrainWord = terrain === 'FLATLAND' ? t('wertung.terrainFlatland') : t('wertung.terrainSlope')
                return (
                  <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-3 md:p-4 mb-3">
                    <FglRaceTrack
                      terrain={terrain}
                      view={raceView}
                      label={`${terrainWord} · ${headerWord} · ${t('wertung.seasonLabel', { season: 2026 })}`}
                      height={64}
                      topN={30}
                    />
                  </div>
                )
              })()}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {data.data.columns.map(col => (
                  <BucketColumn key={col.bucket} column={col} classFilter={classFilter} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  )
}

function BucketColumn({
  column,
  classFilter,
}: {
  column: BucketsApiColumn
  classFilter: Klasse | 'all'
}) {
  const { t } = useTranslation()
  return (
    <div className="rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-2">
      <div className="flex items-center justify-between mb-2 px-1.5">
        <span className="text-[11px] font-black uppercase tracking-wider text-[var(--accent)]">
          {t(`wertung.bucket.${column.bucket}`)}
        </span>
        <span className="text-[9px] font-bold text-[var(--muted)] tabular-nums">
          ({column.flightCount})
        </span>
      </div>
      {column.rows.length === 0 ? (
        <div className="text-center py-4 text-[9px] text-[var(--muted)] italic">
          {t('wertung.noFlights')}
        </div>
      ) : (
        <div className="space-y-1">
          {column.rows.map(r => (
            <BucketPilotRow key={r.pilot.userId} entry={r} classFilter={classFilter} />
          ))}
        </div>
      )}
    </div>
  )
}

function BucketPilotRow({
  entry,
  classFilter,
}: {
  entry: BucketsApiPilot
  classFilter: Klasse | 'all'
}) {
  const { t } = useTranslation()
  // r19.44 (Stephan-Spec 2026-06-25, Variante 1 „Glas-Kacheln mit Hover-Card"):
  // Default-View ist EINE Zeile (Rang + Avatar + Name + Σ-km). Pro Pilot KEINE
  // Zahlenkolonne mehr darunter (das war der rcmodelspot-Look). Stattdessen
  // erscheint bei Hover eine schwebende Card mit den beitragenden Flügen als
  // klickbare Glas-Pillen. Mobile: Tap toggelt die Card inline.
  const missing = MIN_FLIGHTS_FOR_CLASS - entry.classFlightCount
  const classLabel =
    classFilter === 'CLUBCLASS' ? t('wertung.classClub')
    : classFilter === 'STANDARD' ? t('wertung.classStandard')
    : classFilter === 'OPEN' ? t('wertung.classOpen')
    : null
  const tooltipText = entry.meetsClassMin
    ? t('wertung.bucketPilotTooltip', { name: entry.pilot.name })
    : (classLabel
        ? (missing === 1
            ? t('wertung.candidateTooltipBucketOne', { name: entry.pilot.name, klasseLabel: classLabel })
            : t('wertung.candidateTooltipBucketMany', { missing, name: entry.pilot.name, klasseLabel: classLabel }))
        : (missing === 1
            ? t('wertung.candidateTooltipOne', { name: entry.pilot.name })
            : t('wertung.candidateTooltipMany', { missing, name: entry.pilot.name }))
      )

  const [open, setOpen] = useState(false)
  // r19.44.1 (Stephan-Spec 2026-06-25): Mini-Sort-Toggle direkt IN der Hover-
  // Card pro Person — pro Pilot eigener State. 'km' (default) sortiert nach
  // Stage-Distance absteigend, 'class' gruppiert CL → ST → OP, innerhalb der
  // Klasse nach km absteigend.
  type FlightSort = 'km' | 'class'
  const [flightSort, setFlightSort] = useState<FlightSort>('km')

  // Top-3 → Gold/Silber/Bronze-Trophäen-Glas im Rang-Badge.
  const rankColor =
    entry.rank === 1 ? '#f2c94e' :
    entry.rank === 2 ? '#cfd6e0' :
    entry.rank === 3 ? '#cd7f32' :
    'var(--muted)'

  // contributingFlights nach gewähltem Modus
  const CLASS_ORDER: Record<string, number> = { CLUBCLASS: 0, STANDARD: 1, OPEN: 2 }
  const flightsSorted = [...entry.contributingFlights].sort((a, b) => {
    if (flightSort === 'class') {
      const ca = CLASS_ORDER[a.classCode ?? ''] ?? 9
      const cb = CLASS_ORDER[b.classCode ?? ''] ?? 9
      if (ca !== cb) return ca - cb
    }
    return b.distanceKm - a.distanceKm
  })

  return (
    <div
      className={`relative group ${entry.meetsClassMin ? '' : 'opacity-40'}`}
      onMouseEnter={() => entry.meetsClassMin && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-tip={tooltipText}
    >
      <button
        type="button"
        onClick={() => entry.meetsClassMin && setOpen(v => !v)}
        disabled={!entry.meetsClassMin}
        className={`w-full text-left flex items-center gap-2 px-1.5 py-1.5 rounded-md ${
          entry.meetsClassMin
            ? 'hover:bg-white/[0.04] cursor-pointer'
            : 'cursor-default'
        } transition-colors`}
      >
        <span
          className="text-[10px] font-black tabular-nums w-4 text-left shrink-0"
          style={{ color: rankColor }}
        >
          {entry.rank}
        </span>
        <DonorAvatar
          src={entry.pilot.avatarUrl}
          name={entry.pilot.name}
          donorTier={entry.pilot.donorTier}
          size={32}
          showBadge={false}
        />
        <div className="flex-1 min-w-0">
          <div className="truncate text-[11px] font-bold leading-tight text-[var(--text)] group-hover:text-[var(--accent)] transition-colors">
            {entry.pilot.name}
          </div>
          <div className="flex items-center gap-1 leading-tight mt-0.5">
            <FlagIcon iso={entry.pilot.countryCode} size={10} />
            {entry.pilot.clubName && (
              <span className="truncate text-[8.5px] text-[var(--muted)]">
                {entry.pilot.clubName}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end shrink-0 leading-tight">
          <span className="text-[11px] font-black text-[var(--accent)] tabular-nums">
            {fmtKm(entry.scoreKm)}
          </span>
          {entry.avgKmh != null && (
            <span className="text-[8.5px] text-[var(--muted)] tabular-nums">
              ⌀ {entry.avgKmh.toFixed(0)} km/h
            </span>
          )}
        </div>
      </button>

      {/* r19.44 Hover-/Tap-Card: Floating-Glas mit klickbaren Flug-Pillen. */}
      {open && entry.meetsClassMin && flightsSorted.length > 0 && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 rounded-xl p-2 shadow-2xl"
          style={{
            background: 'color-mix(in oklch, var(--bg-card) 92%, transparent)',
            backdropFilter: 'blur(12px)',
            border: '1px solid color-mix(in oklch, var(--accent) 30%, var(--border))',
            boxShadow: '0 12px 32px rgba(0,0,0,.35), 0 0 0 1px color-mix(in oklch, var(--accent) 18%, transparent) inset',
          }}
        >
          {/* r19.44.1: Sortier-Toggle direkt in der Card, gilt nur für diese Person. */}
          <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
            <span className="text-[8.5px] uppercase tracking-[.16em] font-black text-[var(--muted)]">
              {t('wertung.flightsExpandHint')}
            </span>
            <div className="flex items-center gap-1">
              {(['km', 'class'] as const).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={e => { e.stopPropagation(); setFlightSort(s) }}
                  data-state={flightSort === s ? 'active' : 'inactive'}
                  className="wertung-tab-pill px-2 py-0.5 rounded-full text-[8.5px] font-black uppercase tracking-wider"
                  data-tip={s === 'km' ? t('wertung.sortByKmTooltip') : t('wertung.sortByClassTooltip')}
                >
                  {s === 'km' ? t('wertung.sortByKm') : t('wertung.sortByClass')}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            {flightsSorted.map((f, i) => (
              <Link
                key={f.flightId + i}
                href={`/flight/${f.flightId}`}
                className="flex items-center gap-1.5 px-1.5 py-1 rounded-md transition-colors hover:bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <span className="text-[10px] font-black tabular-nums text-[var(--accent)] w-12 shrink-0">
                  {f.distanceKm.toFixed(1)}
                </span>
                <span className="text-[9px] tabular-nums text-[var(--muted)] w-12 shrink-0">
                  {f.avgKmh != null ? `${Math.round(f.avgKmh)} km/h` : '—'}
                </span>
                {f.classCode && (
                  <span
                    className="text-[8px] font-black uppercase tracking-tight px-1 py-0.5 rounded shrink-0"
                    style={{
                      background:
                        f.classCode === 'CLUBCLASS' ? 'rgba(242,201,78,.18)' :
                        f.classCode === 'STANDARD'  ? 'rgba(96,165,250,.18)' :
                        'rgba(168,85,247,.18)',
                      color:
                        f.classCode === 'CLUBCLASS' ? '#f2c94e' :
                        f.classCode === 'STANDARD'  ? '#60a5fa' :
                        '#c084fc',
                    }}
                  >
                    {f.classCode === 'CLUBCLASS' ? 'CL' : f.classCode === 'STANDARD' ? 'ST' : 'OP'}
                  </span>
                )}
                <span className="text-[8.5px] text-[var(--muted)] truncate flex-1">
                  {new Date(f.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// 5-Flüge-Pflicht-Konstante (synchron mit WertungService.MIN_FLIGHTS_PER_CLASS)
const MIN_FLIGHTS_FOR_CLASS = 5

// R103.38: Subtype-Chip fuer EinzelflugRowCard (Mini-SVG-Icon + Klartext)
// r19.55.219: label via resolveSubtypeDisplay (i18n first, then DB, then default).
function EinzelflugSubtypeChip({ subtype }: { subtype: string | null }) {
  const { t } = useTranslation()
  const registry = useSubtypesRegistry()
  if (!subtype) return null
  const meta = registry[subtype]
  const label = resolveSubtypeDisplay(t, subtype, meta?.displayName) || subtype
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border"
      style={{
        color: meta?.colorHex || 'var(--muted)',
        borderColor: meta?.colorHex ? `${meta.colorHex}55` : 'var(--border)',
        background: meta?.colorHex ? `${meta.colorHex}11` : 'transparent',
      }}
      title={label}
    >
      <TerrainSubtypeIcon subtype={subtype} size={12} />
      <span className="hidden lg:inline">{label}</span>
    </span>
  )
}

function EinzelflugList({ data }: { data: EinzelflugResult }) {
  const { t } = useTranslation()
  const loggedIn = useLoggedIn()
  const rankLbl = useRankLabel()
  if (data.flights.length === 0) {
    return (
      <div className="text-center py-16 text-[var(--muted)]">
        <Maximize2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <div className="text-sm">{t('wertung.noScoringFlights')}</div>
      </div>
    )
  }
  return (
    <div className="max-w-6xl mx-auto space-y-3">
      <section className="space-y-3">
        {data.flights.map(({ rank, pilot, flight }) => (
          <EinzelflugRowCard
            key={flight.flightId}
            rank={rank}
            pilot={pilot}
            flight={flight}
            loggedIn={loggedIn}
            rankLbl={rankLbl}
          />
        ))}
      </section>
    </div>
  )
}

// R102.7.8 (Stephan-Wunsch 2026-06-01 nachts): Bester-Einzelflug-Liste in
// dasselbe SailGP-Look-Design wie die TOP-10 (PilotRowCard). Pro Eintrag
// ein Flug; Layout: [Rank-Strip] · [Trophy] · [Avatar] · [Name+Untertitel]
// · [Klassen-Badge] · [Speed-Pillen] · [Flag] · [Score-km].
function EinzelflugRowCard({
  rank, pilot, flight, loggedIn, rankLbl,
}: {
  rank: number
  pilot: EinzelflugPilot
  flight: EinzelflugFlight
  loggedIn: boolean
  rankLbl: (n: number) => string
}) {
  const glowFilter = (() => {
    if (rank === 1) return 'drop-shadow(0 0 14px rgba(242,201,78,0.50)) drop-shadow(0 6px 20px rgba(0,0,0,0.22))'
    if (rank === 2) return 'drop-shadow(0 0 14px rgba(225,225,235,0.45)) drop-shadow(0 6px 20px rgba(0,0,0,0.22))'
    if (rank === 3) return 'drop-shadow(0 0 14px rgba(205,127,50,0.45)) drop-shadow(0 6px 20px rgba(0,0,0,0.22))'
    if (rank <= 10) {
      const intensity = Math.max(0.10, 0.36 - (rank - 3) * 0.04)
      return `drop-shadow(0 0 ${22 - (rank - 3) * 2}px rgba(242,201,78,${intensity})) drop-shadow(0 4px 16px rgba(0,0,0,0.18))`
    }
    return 'drop-shadow(0 4px 14px rgba(0,0,0,0.18))'
  })()

  const router = useRouter()
  const parts = (pilot.name || '').trim().split(/\s+/).filter(Boolean)
  const last = parts.length > 0 ? parts[parts.length - 1] : ''
  const first = parts.length > 1 ? parts.slice(0, -1).join(' ') : ''
  const nameStyle: React.CSSProperties = {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(20px, 2.6vw, 28px)',
    letterSpacing: '-0.015em',
    lineHeight: 1.05,
    textTransform: 'uppercase',
  }
  const nameInner = (
    <span className="truncate">
      {first && <span style={{ fontWeight: 400 }}>{first} </span>}
      <span style={{ fontWeight: 800 }}>{last}</span>
    </span>
  )
  const canLink = loggedIn && !!pilot.isPublic && !!pilot.slug

  return (
    <Reveal>
      <div
        className={`relative ${rank <= 3 ? rankSpinClass(rank) : ''}`}
        style={{ filter: glowFilter }}
      >
        {/* R106.3 (Stephan-Spec 2026-06-07): gesamte Karte klickbar → Flug,
            Pilot-Name extra → Pilotenseite (stopPropagation). Kein nested
            Link weil HTML-invalid. */}
        <div
          role="link"
          tabIndex={0}
          onClick={() => router.push(`/flight/${flight.flightId}`)}
          onKeyDown={(e) => { if (e.key === 'Enter') router.push(`/flight/${flight.flightId}`) }}
          className="block relative overflow-hidden border bg-[var(--bg-card)] transition-all hover:brightness-110 cursor-pointer"
          style={{
            ...rankStyle(rank),
            clipPath: [
              'polygon(',
              '26px 0,',
              'calc(100% - 14px) 0,',
              'calc(100% - 6px) 2px,',
              'calc(100% - 2px) 6px,',
              '100% 14px,',
              '100% 100%,',
              'calc(100% - 26px) 100%,',
              '14px 100%,',
              '6px calc(100% - 2px),',
              '2px calc(100% - 6px),',
              '0 calc(100% - 14px),',
              '0 0',
              ')',
            ].join(' '),
          }}
        >
          {tierForRank(rank) && <TopRankBubbles tier={tierForRank(rank)!} />}
          <div
            className="flex items-center gap-2 sm:gap-3 pl-0 py-3"
            style={{ paddingRight: '32px' }}
          >
            {/* SailGP-Style Rank-Strip */}
            <div
              className="flex items-center justify-center self-stretch shrink-0 tabular-nums"
              style={{
                background: 'linear-gradient(180deg, color-mix(in oklch, var(--award-gold) 32%, transparent), color-mix(in oklch, var(--award-gold) 18%, transparent))',
                color: 'var(--award-gold)',
                minWidth: rank < 10 ? '88px' : '112px',
                paddingLeft: '26px',
                paddingRight: '12px',
                fontFamily: 'var(--font-display)',
                fontWeight: 800,
                fontSize: rank < 10 ? 'clamp(44px, 7vw, 64px)' : 'clamp(36px, 5.5vw, 52px)',
                lineHeight: 1,
                letterSpacing: '-0.02em',
                borderRight: '1px solid color-mix(in oklch, var(--award-gold) 25%, transparent)',
              }}
            >
              {rank}
            </div>

            {rank <= 3 && (
              <div className="hidden sm:flex items-center shrink-0 ml-1">{trophyForRank(rank)}</div>
            )}

            {/* Avatar */}
            <div className="shrink-0 sm:hidden">
              <DonorAvatar src={pilot.avatarUrl} name={pilot.name} donorTier={pilot.donorTier} size={36} />
            </div>
            <div className="hidden sm:block shrink-0">
              <DonorAvatar src={pilot.avatarUrl} name={pilot.name} donorTier={pilot.donorTier} size={52} />
            </div>

            {/* Name + Untertitel + Flag + Score */}
            <div className="flex-1 min-w-0 flex items-stretch">
              <div className="flex-1 min-w-0 flex flex-col justify-center">
                <div className="flex items-stretch gap-2 sm:gap-3">
                  {canLink ? (
                    <Link
                      href={pilotHref(pilot.slug, pilot.userId)}
                      onClick={e => e.stopPropagation()}
                      className="flex items-center gap-1.5 flex-1 min-w-0 text-[var(--text)] hover:text-[var(--accent)] transition-colors"
                      style={nameStyle}
                    >
                      {nameInner}
                      {rank <= 3 && <span className="shrink-0 sm:hidden">{trophyForRank(rank)}</span>}
                    </Link>
                  ) : (
                    <div
                      className="flex items-center gap-1.5 flex-1 min-w-0 text-[var(--text)]"
                      style={nameStyle}
                    >
                      {nameInner}
                      {rank <= 3 && <span className="shrink-0 sm:hidden">{trophyForRank(rank)}</span>}
                    </div>
                  )}
                </div>
                {rank > 1 && rank <= 10 && (
                  <div className="text-[9px] text-[var(--muted)] uppercase tracking-widest font-bold mt-1">
                    {rankLbl(rank)}
                  </div>
                )}
              </div>

              {/* Klassen-Badge + Subtype-Chip (R103.38) + Speed-Pillen (klein, vor der Flagge) */}
              <div className="hidden sm:flex items-center gap-2 self-center shrink-0 mr-3">
                {flight.classCode && <ClassBadge classCode={flight.classCode} />}
                <EinzelflugSubtypeChip subtype={flight.terrainSubtype || null} />
                <SpeedBatchPills batches={flight.speedBatches} />
              </div>

              {/* Flag in voller Höhe, vor dem Score */}
              {pilot.countryCode && /^[a-z]{2}$/i.test(pilot.countryCode) && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={`${FLAG_BASE}${pilot.countryCode.toLowerCase()}.svg`}
                  alt={pilot.countryCode.toUpperCase()}
                  className="hidden sm:block shrink-0 object-cover self-center"
                  style={{
                    height: '52px',
                    width: '78px',
                    borderRadius: 4,
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.18)',
                  }}
                  loading="lazy"
                />
              )}

              {/* Score */}
              <div className="flex flex-col items-end justify-center shrink-0 pl-2 sm:pl-3">
                <div className="text-base sm:text-2xl font-bold sm:font-black text-[var(--accent)] tabular-nums leading-none">
                  {fmtKm(flight.distanceKm)}
                </div>
                <div className="text-[9px] sm:text-[10px] text-[var(--muted)] uppercase tracking-widest font-bold mt-1">
                  km
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  )
}

type EinzelflugPilot = EinzelflugResult['flights'][number]['pilot']
type EinzelflugFlight = EinzelflugResult['flights'][number]['flight']
